/* Russian UI catalogue.
 *
 * Authored, not translated. Russian is first-class here (EXPERIENCE.md
 * § Voice and Tone, FR-58), so these strings use the trade's own vocabulary:
 *
 *   Работа        the central entity — the word already on their Master File
 *                 sheet tab. NOT «кейс», NOT «сделка». EXPERIENCE.md fixes the
 *                 UI term and overrides the RU brief, which says «сделка».
 *   КП            the client-facing quotation document, as everyone says it
 *                 aloud. The list surface is «Котировки».
 *   Ставка        the approved client rate.
 *   Расчётные /   planned vs actual costs — the Master File's own headings.
 *   Фактические затраты
 *   Направление   one leg of a multimodal shipment. The UI word changed from
 *                 «Плечо» (issue #35): a leg now carries a named route copied
 *                 from the Settings catalogue of «Направления», and one word
 *                 for the catalogue and another for what it fills would be two
 *                 names for one thing. Every IDENTIFIER stays `leg`/`legs` —
 *                 the table, the endpoint, the DTO and the keys below — because
 *                 renaming a contract to match a label is how the two stop
 *                 being traceable to each other.
 *
 * Russian plurals take three forms and i18next's CLDR suffixes carry them:
 * `_one` (1, 21, 31…), `_few` (2–4, 22–24…), `_many` (0, 5–20, 25…). A day
 * counter is the visible case: «4 дня» but «5 дней».
 *
 * Length: Russian runs roughly 15–30% longer than English. Nothing here may
 * assume the shorter one — no layout in the product is sized to the English
 * string (EXPERIENCE.md) — and table headers in particular are kept short
 * because a column header that wraps changes the row height of a dense grid.
 */

export const ru = {
  common: {
    appName: 'ASA Gateway',
    empty: '—',
    actions: {
      retry: 'Повторить',
      retryNow: 'Повторить сейчас',
      dismiss: 'Скрыть',
      undo: 'Отменить',
      cancel: 'Отмена',
      save: 'Сохранить',
      edit: 'Изменить',
      add: 'Добавить',
      open: 'Открыть',
      close: 'Закрыть',
      delete: 'Удалить',
      upload: 'Загрузить',
      /** Название колонки действий, заголовок которой визуально пуст. */
      label: 'Действия',
      back: 'На дашборд',
    },
    /** Переключатель таблица/доска на списках. */
    view: {
      label: 'Вид',
      table: 'Таблица',
      board: 'Доска',
    },
    /* Две собственные настройки плотной таблицы (UX-DR-4). Обе — привычка
       рабочего места, своя у каждого пользователя и у каждого экрана, поэтому
       текст говорит про ЭТОТ ЭКРАН, а не про настройки профиля. */
    columns: {
      label: 'Колонки',
      description: 'Какие колонки показывает этот экран',
      reset: 'Показать все колонки',
      hiddenCount_one: 'Скрыта {{count}} колонка',
      hiddenCount_few: 'Скрыто {{count}} колонки',
      hiddenCount_many: 'Скрыто {{count}} колонок',
    },
    filters: {
      label: 'Фильтр',
      status: 'Статус',
      all: 'Все',
      clear: 'Сбросить фильтр',
      /* Множественное число и отдельный ключ: на финансовом экране одновременно
         стоят период, фасет и поиск, и кнопка «Сбросить фильтр» заставляет
         гадать, какой из трёх она уберёт. */
      clearAll: 'Сбросить фильтры',
      activeCount_one: '{{count}} фильтр',
      activeCount_few: '{{count}} фильтра',
      activeCount_many: '{{count}} фильтров',
      emptyTitle: 'Под фильтр ничего не подходит',
      emptyDescription: 'Сбросьте фильтр, чтобы снова увидеть весь список.',
    },
    /* Два пустых состояния плотной таблицы (UX-DR-17). Здесь — «ещё ничего
       нет»; «ничего не подходит» берётся из `filters.emptyTitle` выше, чтобы
       два прочтения пустой таблицы оставались одним решением. Текст намеренно
       общий: это запасной вариант, а экран, который может сказать, чем он
       наполняется, передаёт свой собственный. */
    table: {
      emptyTitle: 'Здесь пока пусто',
      emptyDescription: 'Строки появятся здесь по мере их создания.',
    },
    /* Подвал таблицы со страницами. «Строк на странице» — единственное место,
       где продукт говорит «строки», а не называет запись: это факт о таблице,
       а не о котировках в ней. Счётчик рядом («6 котировок») приходит с
       экрана, который знает, что именно считает. */
    pagination: {
      label: 'Страницы',
      rowsPerPage: 'Строк на странице:',
      previous: 'Предыдущая страница',
      next: 'Следующая страница',
      page: 'Страница {{page}}',
      position: '{{page}} / {{total}}',
    },
    language: {
      label: 'Язык интерфейса',
      en: 'EN',
      ru: 'RU',
      english: 'English',
      russian: 'Русский',
    },
    search: {
      label: 'Поиск',
      placeholder: 'Поиск по работам, котировкам, клиентам',
      loading: 'Ищем…',
      emptyTitle: 'Ничего не нашлось',
      emptyDescription: 'Проверьте номер или попробуйте часть названия клиента.',
      resultCount_one: '{{count}} совпадение',
      resultCount_few: '{{count}} совпадения',
      resultCount_many: '{{count}} совпадений',
      hintNavigate: 'выбрать',
      hintOpen: 'открыть',
      hintClose: 'закрыть',
      kind: {
        case: 'Работа',
        quote: 'Котировка',
        client: 'Клиент',
      },
    },
    notifications: {
      label: 'Уведомления',
      labelWithCount_one: 'Уведомления, {{count}} непрочитанное',
      labelWithCount_few: 'Уведомления, {{count}} непрочитанных',
      labelWithCount_many: 'Уведомления, {{count}} непрочитанных',
      emptyTitle: 'Ничего нового',
      emptyDescription:
        'Здесь появятся ответы в чате, смены статуса, согласования и платежи по вашим котировкам и работам.',
  loading: 'Загрузка уведомлений',
      markAllRead: 'Отметить все',
      unreadItem: 'Не прочитано — {{kind}}',
    },
    /** Column and field labels shared by more than one table. */
    fields: {
      name: 'Название',
      country: 'Страна',
      contact: 'Контакт',
      email: 'Email',
      phone: 'Телефон',
      owner: 'Ответственный',
      type: 'Тип',
      status: 'Статус',
      amount: 'Сумма',
      number: 'Номер',
      total: 'Итого',
      date: 'Дата',
    },
    form: {
      saving: 'Сохранение…',
      required: 'Обязательное поле',
      /* Коды полей, которые сервер присылает стабильными токенами (AD-27). */
      invalid: 'Значение не принято',
      tooLong: 'Слишком длинное значение',
      malformed: 'Не удалось прочитать значение',
      invalidNumber: 'Введите число',
      /* Число само по себе верное, но точнее, чем колонка, которая его хранит.
         Отдельная строка: «Введите число» над 3.607 отправит читателя искать
         ошибку, которой нет. */
      tooManyDecimals: 'Не более 2 знаков после запятой',
      fixFields: 'Проверьте поля — без этого сохранить нельзя.',
      active: 'Активен',
      inactive: 'Неактивен',
      optional: 'Необязательно',
      /* Подпись на каждом раскрывающемся блоке, который прячет остаток формы.
         Одно слово на всех, чтобы тот, кто нашёл редкие поля в одной форме,
         искал их там же в следующей. Число рядом — от компонента. */
      more: 'Ещё',
      selectPlaceholder: 'Выберите…',
      searchPlaceholder: 'Поиск…',
      noMatches: 'Ничего не найдено',
      noMatchesHint: 'Попробуйте запрос короче',
      /* Выбор страны (ISO 3166-1). Закрытый элемент теперь кнопка, поэтому
         подсказка называет сам выбор; о том, что можно ввести, говорит поле
         поиска в открытом списке — строка ниже. */
      countryPlaceholder: 'Выберите страну…',
      /* Поиск внутри списка стран. Ищет и по названию, и по кодам, поэтому
         подсказка называет оба: по «Поиск…» не догадаться, что «RUS» или
         «398» тоже находят страну. */
      countrySearch: 'Страна, код…',
      /* Две половины поля телефона: кнопка кода страны и номер рядом с ней.
         Сама кнопка показывает «+7» и не говорит, что это, — существительное
         даёт подпись. */
      phoneCode: 'Телефонный код страны',
      phoneSearch: 'Страна, +код…',
      phonePlaceholder: 'Номер телефона',
      /* Для сохранённого значения, которого нет среди 249 кодов, — порта или
         города, введённого до появления выбора. Остаётся доступным: запись не
         ошибочна, она старше самого поля. */
      countryCarriedOver: 'как введено',
      /* Русский требует три формы: 1 показан, 2–4 показаны, 5+ показано.
         i18next выбирает ветку по `count`, поэтому текст согласуется с числом
         совпадений, а не с общим количеством. */
      matchCount_one: 'Показан {{count}} из {{total}}',
      matchCount_few: 'Показаны {{count}} из {{total}}',
      matchCount_many: 'Показано {{count}} из {{total}}',
      clear: 'Очистить',
      // Формат остаётся латиницей: это маска ввода, а не текст — поле хранит
      // и принимает ровно `YYYY-MM-DD`, и «ГГГГ-ММ-ДД» описывал бы форму,
      // которую поле не примет.
      datePlaceholder: 'YYYY-MM-DD',
      openCalendar: 'Открыть календарь',
      previousMonth: 'Предыдущий месяц',
      nextMonth: 'Следующий месяц',
      today: 'Сегодня',
    },
    /* Предупреждение о несохранённых изменениях в форме.
     *
     * Вопрос, а не предупреждение, и в нём названо, чем рискуют: «вы уверены?»
     * без указания потери заставляет догадываться, с чем соглашаешься.
     * Основное действие здесь — ОТМЕНИТЬ закрытие, безопасное, чтобы
     * машинальный Enter сохранял черновик, а не удалял его. */
    unsaved: {
      title: 'Отменить изменения?',
      body: 'В форме есть несохранённые правки. Если её закрыть, они пропадут.',
      keep: 'Вернуться к правке',
      discard: 'Отменить изменения',
    },
    menu: {
      open: 'Открыть меню',
      close: 'Закрыть меню',
    },
    loading: 'Загрузка',
    /* Один словарь периодов на все финансовые экраны и дашборд
       (components/data/DateRangeControl.tsx). `1W` убран вместе со вторым
       словарём, который его объявлял: неделя дебиторки — это шум вокруг одного
       счёта, а два набора периодов позволяют двум экранам отвечать на разные
       вопросы, выглядя одинаково. */
    dateRange: {
      label: 'Период: {{label}}',
      shared: 'Период',
      // Range chips stay Latin: they are read as symbols, they must fit a
      // narrow chip, and «1Н / 1М / 3М / 1Г» reads as a typo at 11.5px.
      '1M': '1M',
      '3M': '3M',
      '1Y': '1Y',
      all: 'Всё',
    },
    /* Suppressed fields (AD-6): the UI names the suppression rather than
     * rendering an empty cell, so it is never misread as missing data. */
    suppressed: {
      agent: 'Агент скрыт',
      carrier: 'Перевозчик скрыт',
      vendor: 'Поставщик скрыт',
      contact: 'Контакты ограничены',
      generic: 'Скрыто',
      reason: 'Ваша роль не видит это поле',
    },
    relativeTime: {
      now: 'сейчас',
      minutes: '{{count}} мин',
      hours: '{{count}} ч',
      days: '{{count}} д',
    },
  },

  nav: {
    label: 'Основное',
    sections: {
      main: 'Основное',
      company: 'Компания',
    },
    dashboard: 'Дашборд',
    quotes: 'Котировки',
    cases: 'Работы',
    chat: 'Чат',
    finance: 'Финансы',
    clients: 'Клиенты',
    directories: 'Справочники',
    settings: 'Настройки',
    profile: 'Профиль',
  },

  roles: {
    sales: 'Продажи',
    pricing: 'Расчёт',
    operations: 'Операции',
    finance: 'Финансы',
    'super-admin': 'Суперадмин',
  },

  modes: {
    sea: 'Море',
    rail: 'Ж/д',
    road: 'Авто',
    air: 'Авиа',
    multimodal: 'Мультимодал',
  },

  caseStatus: {
    draft: 'Черновик',
    'in-progress': 'В работе',
    stuck: 'Застряла',
    delivered: 'Доставлена',
    // Двухэтапное закрытие, по которому работает компания (FR-25): сначала
    // операционное, потом финансовое. Обе половины НАЗЫВАЮТ себя: просто
    // «Закрыта» рядом с «Закрыта опер.» заставляла читателя догадываться, что
    // неуточнённая — это про деньги, а название статуса существует ровно для
    // того, чтобы догадываться не приходилось.
    'operationally-closed': 'Закрыта опер.',
    'financially-closed': 'Закрыта фин.',
    cancelled: 'Отменена',
  },

  quoteStatus: {
    draft: 'Черновик',
    sent: 'Отправлена',
    rejected: 'Отклонена',
    expired: 'Истекла',
    converted: 'Конвертирована',
  },

  docStatus: {
    draft: 'Черновик',
    issued: 'Выставлен',
    'part-paid': 'Частично оплачен',
    paid: 'Оплачен',
    /* Значения `void` здесь нет, и это намеренно. AD-24 делает переход
       «черновик → выставлен» односторонним, и вернуть документ обратно в
       системе нечем: исправление оформляется новым документом — ровно так,
       как это делается на бумаге. Плашка состояния, которое никто не может
       создать, обещает то, чего не бывает. `part-paid` и `paid` тоже нигде не
       хранятся — они выводятся на клиенте из присланных сумм, — но это
       настоящие состояния настоящего документа, поэтому они остались, а
       `void` ушёл. */
  },

  documentKind: {
    invoice: 'Счёт',
    'debit-note': 'Дебет-нота',
    // КП — как это и называют вслух.
    offer: 'КП',
  },

  legStatus: {
    planned: 'Запланировано',
    'in-transit': 'В пути',
    'waiting-border': 'Ожидает на границе',
    arrived: 'Прибыло',
    completed: 'Завершено',
  },

  participantRole: {
    shipper: 'Грузоотправитель',
    consignee: 'Грузополучатель',
    agent: 'Агент',
    'counter-agent': 'Контрагент',
    'vehicle-owner': 'Владелец транспорта',
    dispatcher: 'Диспетчер',
    driver: 'Водитель',
  },

  notificationKind: {
    message: 'Сообщение',
    status: 'Статус',
    approval: 'Согласование',
    payment: 'Платёж',
    document: 'Документ',
    file: 'Файл',
    assignment: 'Назначение',
    stuck: 'Застряла',
  },

  recordKind: {
    case: 'Работа',
    quote: 'Котировка',
    invoice: 'Счёт',
  },

  dashboard: {
    /* Russian splits the day differently from English: «Добрый день» covers
     * roughly 11:00–17:00 and there is no idiomatic equivalent of a broad
     * "Good afternoon". The boundaries live in the component; both catalogues
     * simply supply three greetings and the component picks by hour. */
    greeting: {
      morning: 'Доброе утро, {{name}}',
      afternoon: 'Добрый день, {{name}}',
      evening: 'Добрый вечер, {{name}}',
    },
    eyebrow: '{{date}} · Данные на {{time}}',
    subtitle_one: '{{count}} вопрос требует решения сегодня — по перевозкам и деньгам.',
    subtitle_few: '{{count}} вопроса требуют решения сегодня — по перевозкам и деньгам.',
    subtitle_many:
      '{{count}} вопросов требуют решения сегодня — по перевозкам и деньгам.',
    loading: 'Загрузка дашборда',

    counters: {
      activeCases: 'Активные работы',
      shipmentsThisMonth: 'Отгрузки за месяц',
      toPay: 'К оплате',
    },

    /* Денежные плитки. Подпись здесь не обещает, что цифра есть: оборот сервер
       по-прежнему объявляет `pending`, и плитка сама говорит о своём
       отсутствии, а не рисует пустую карточку. */
    tiles: {
      turnover: 'Оборот',
      /* «Прибыль», а не «Прогноз прибыли»: по FR-51 AC 2 это одна цифра на двух
         основаниях — сколько работа заработала фактически, если она финансово
         закрыта, и сколько прогнозируется по всем остальным. Название по одному
         из двух неверно описывало бы вторую половину той же суммы. */
      profit: 'Прибыль',
      /* «К получению» и «К оплате» — слова самого основателя, и каждое сразу
         говорит, что с этим делать. */
      toCollect: 'К получению',
      toPay: 'К оплате',
      /* Переход к списку за цифрой (UX-DR-10). Одна подпись на все плитки:
         заголовок плитки уже называет цифру, и «Смотреть дебиторку» рядом с
         «К получению» сказало бы это дважды. */
      drillThrough: 'Открыть список',
    },

    pending: {
      description: 'Появится после истории {{story}}.',
    },

    attention: {
      title: 'Требует решения',
      // Where nothing is wrong, say so plainly — never encourage.
      emptyTitle: 'Ничего не требует решения',
      /* Порог приходит с сервера. Три формы, потому что счёт по-русски требует
         «1 день», «2 дня», «5 дней» — форма _many покрывает и 0. */
      emptyDescription_one:
        'Работа попадёт сюда, если не двигалась {{count}} день.',
      emptyDescription_few:
        'Работа попадёт сюда, если не двигалась {{count}} дня.',
      emptyDescription_many:
        'Работа попадёт сюда, если не двигалась {{count}} дней.',
      stuckFor_one: 'Без движения {{count}} день',
      stuckFor_few: 'Без движения {{count}} дня',
      stuckFor_many: 'Без движения {{count}} дней',
      /* Какая именно цифра отрицательная (FR-52). Различие определяет действие:
         прогноз ещё можно исправить, пока работа идёт, а итог — это убыток,
         который уже проведён. */
      minusBasis: {
        forecast: 'Прогноз убыточный',
        final: 'Закрыта с убытком',
      },
      kind: {
        stuck: 'Застряла',
        minus: 'Минус',
        approval: 'Согласование',
        overdue: 'Просрочено',
        checklist: 'Неполные данные',
        'quote-followup': 'Напомнить',
      },
    },

    activity: {
      title: 'Последние события',
      empty: 'Событий пока нет',
    },

    aging: {
      caption: 'Клиентов: {{clients}} · траншей на этой неделе: {{tranches}}',
    },
  },

  cases: {
    title: 'Работы',
    subtitle: 'Все перевозки в движении и сколько они стоят.',
    loading: 'Загрузка работы',
    backToList: 'К работам',
    // Пока карточка работы собирается из модулей, которых ещё нет, открывать
    // её нечем — честнее сказать это, чем показать «запись недоступна».
    detailNotReady: 'Карточка работы ещё не подключена — {{number}} создана и есть в списке.',
    sectionsLabel: 'Разделы работы',
    actions: {
      fromQuote: 'Из котировки',
      newCase: 'Новая работа',
      editCase: 'Изменить работу',
      create: 'Создать работу',
      sendStatus: 'Отправить статус клиенту',
    },

    /* FR-41. «Отправить», не «уведомить»: отправляет человек, а не система. */
    statusSend: {
      title: 'Отправить статус клиенту',
      submit: 'Отправить',
      recipient: 'Кому',
      status: 'Статус',
      language: 'Язык письма',
      languageHint: 'Язык, на котором читает клиент, а не вы.',
      note: 'Комментарий',
      noteHint: 'Необязательно. Добавится под статусом и датой прибытия.',
      notePlaceholder: 'Что клиенту стоит знать помимо статуса.',
      noContact:
        'У этого клиента ещё нет контактной почты. Добавьте её в справочнике клиентов, чтобы отправлять обновления.',
      sent: 'Статус отправлен на {{email}}.',
      failed: 'Статус не отправлен. Проверьте соединение и попробуйте снова.',
    },

    /* Русский требует три формы множественного числа (one/few/many), а не две:
       1 работа, 2 работы, 5 работ. Английский `_other` здесь дал бы «5 работы». */
    clientStatusSend: {
      title: 'Отправить статус клиенту',
      client: 'Клиент',
      clientPlaceholder: 'Выберите клиента',
      pickClientFirst: 'Выберите клиента, чтобы увидеть его работы.',
      cases: 'Работы',
      selectAll: 'Выбрать все',
      selectedCount: '{{selected}} из {{total}}',
      submitCount_one: 'Отправить {{count}} работу',
      submitCount_few: 'Отправить {{count}} работы',
      submitCount_many: 'Отправить {{count}} работ',
      noteHint: 'Необязательно. Добавится к каждой работе в этой отправке.',
      noContact:
        'У этого клиента ещё нет контактной почты. Добавьте её в справочнике клиентов, чтобы отправлять обновления.',
      nothingReportable:
        'Все работы этого клиента отменены или закрыты — сообщать не о чем.',
      notReportable: 'Отменена или закрыта — не включена',
      sentAll_one: 'Статус отправлен по {{count}} работе.',
      sentAll_few: 'Статус отправлен по {{count}} работам.',
      sentAll_many: 'Статус отправлен по {{count}} работам.',
      sentPartial: 'Статус отправлен по {{sent}} из {{total}} работ.',
    },

    form: {
      quoteNumber: 'Котировка',
      quoteHint: 'Одобренная котировка, из которой создаётся работа.',
      etd: 'ETD',
      paymentTermsKind: 'Условия оплаты',
      paymentMethod: 'Способ оплаты',
      creditDays: 'Дней отсрочки',
      creditDaysHint: 'Дней от регистрации до срока оплаты всей суммы.',
      creditDaysInvalid: 'Укажите целое число дней, от 1 до 365.',
      nextStep: 'Направления, участники и деньги добавляются в самой работе.',
    },
    terms: {
      fifty_fifty: '50 на 50',
      full_on_delivery: 'Полностью по прибытии',
      credit: 'Отсрочка',
      other: 'Другое',
    },
    method: {
      cash: 'Наличные',
      bank_transfer: 'Банковский перевод',
    },
    columns: {
      number: 'Работа',
      client: 'Клиент',
      route: 'Маршрут',
      leg: 'Направление',
      status: 'Статус',
      eta: 'ETA',
      rate: 'Ставка USD',
      forecast: 'Прогноз USD',
    },
    card: {
      /* Блок цифр на карточке доски. Подписи короткие: их прочитывают один раз,
         а взглядом идут по колонке значений рядом. Даты оставляем как их
         называют на складе и в письмах — ETD и ETA, латиницей. */
      rate: 'Ставка',
      forecast: 'Прогноз',
      /* Где груз на маршруте — на карточке доски. Это ПОЗИЦИЯ, а не обратный
         отсчёт: «Направление 2/3» говорит и сколько пройдено, и сколько
         осталось, тогда как «осталось 1 направление» — только второе, и звучит
         как обещание. */
      leg: 'Направление {{sequence}}/{{total}}',
      etd: 'ETD',
      eta: 'ETA',
      stalledShort: '{{count}} д',
    },

    /* Перенос карточки в другую колонку — это смена статуса (FR-25, AD-24).
       Называем номер работы: пока сообщение читают, карточка уже вернулась
       на место. */
    board: {
      moveRefused:
        'Работа {{number}} не перенесена. Сервер отклонил изменение — для финансового закрытия сначала нужно закрыть все балансы.',
      /* Пустая колонка — словами. Приглашение вынесено в ОТДЕЛЬНЫЙ ключ: оно
         верно только там, куда действительно можно перетащить. Над «Отменена»
         такая строка учила бы жесту, который эта колонка не примет. */
      laneEmpty: 'Нет работ',
      laneDrop: 'Перетащите сюда для изменения статуса.',
    },

    /* Жизненный цикл в самой карточке (FR-25, FR-37, AD-24). Названия статусов
       подставляются из каталога `caseStatus` и берутся в кавычки: «Перевести в
       Отменена» по-русски не читается, а склонять название статуса — значит
       завести ему вторую форму и рассинхронизировать её с колонкой доски. */
    lifecycle: {
      unsettledBalances:
        'Работу нельзя закрыть финансово, пока есть незакрытые расчёты: {{items}}.',
      label: 'Жизненный цикл работы',
      change: 'Изменить статус',
      moveTo: 'Перевести в «{{status}}»',
      moving: 'Переносим…',

      confirmTitle: 'Подтвердите изменение',
      confirmFinancialBody:
        'Финансовое закрытие {{number}} завершает денежную сторону работы: счета и платежи по ней больше не принимаются. Открыть её заново сможет только супер-администратор, и это будет записано в историю.',
      confirmFinancialAction: 'Закрыть финансово',
      confirmCancelBody:
        'Отмена {{number}} необратима. История и связь с котировкой сохранятся, но перевести работу в другой статус после этого будет нельзя.',
      confirmCancelAction: 'Отменить работу',

      balancesTitle: 'Работу пока нельзя закрыть финансово',

      reopen: 'Открыть заново',
      reopenTitle: 'Открыть заново {{number}}',
      reopenSubmit: 'Открыть работу',
      reopenBody:
        'Работа вернётся в статус «Закрыта опер.». Отменяется только финансовое закрытие — доставка заново не открывается.',
      reason: 'Причина',
      reasonHint:
        'Запишется в историю работы вместе с вашим именем. Без причины открыть заново нельзя.',
    },
    stalled_one: 'Нет движения {{count}} день',
    stalled_few: 'Нет движения {{count}} дня',
    stalled_many: 'Нет движения {{count}} дней',
    emptyTitle: 'Работ пока нет',
    emptyDescription:
      'Работы, созданные из котировки или напрямую, появятся здесь со ставкой и прогнозом.',

    header: {
      rate: 'Ставка',
      cost: 'Затраты',
      forecast: 'Прогноз',
      mode: 'Вид перевозки',
    },

    /* «Участники» — это КОМАНДА работы, то есть сотрудники, а стороны сделки
       называются контрагентами. Раньше одно слово стояло на обоих, и вкладка
       «Участники» показывала грузоотправителя с грузополучателем, тогда как
       люди, которые работу ведут, были карточкой на «Обзоре». Два разных
       вопроса — два раздела и два слова. */
    tabs: {
      overview: 'Обзор',
      legs: 'Направления',
      team: 'Участники',
      participants: 'Контрагенты',
      money: 'Деньги',
      documents: 'Счета',
      files: 'Файлы',
      history: 'История',
    },

    overview: {
      route: 'Маршрут',
      /* «жд + авто через Хоргос» — виды транспорта приходят уже переведёнными. */
      routeVia: '{{modes}} через {{border}}',
      stopRole: {
        loading: 'Погрузка',
        border: 'Погранпереход',
        discharge: 'Пункт разгрузки',
        unloading: 'Место выгрузки',
      },
      departed: 'отправление {{date}}',
      arrivedOn: 'прибытие {{date}}',
      hereNow: 'здесь сейчас',
      hereNowFor: 'здесь сейчас, {{days}} дн',
      etaOn: 'ETA {{date}}',

      cargo: 'Груз',
      imo: 'IMO {{class}}',
      kg: 'кг',
      cbm: 'CBM',
      declaredTotal: 'Заявлено всего',
      measuredAt: 'Контрольный вес — {{location}}',

      containers: 'Контейнеры и пломбы',
      containersEmptyTitle: 'Контейнеров пока нет',
      containersEmptyDescription:
        'Пары «контейнер — пломба» подставляются в документы, которые выпускает эта работа.',
      seal: 'Пломба',
      equipment: 'Оборудование',

      details: 'Детали работы',
      dates: 'Даты',
      incoterm: 'Инкотермс',
      paymentTerms: 'Условия оплаты',
      paymentMethod: 'Способ оплаты',
      creditDays_one: '{{count}} день',
      creditDays_few: '{{count}} дня',
      creditDays_many: '{{count}} дней',
      responsible: 'Ответственные',
      opsNamed: 'опер.: {{name}}',
      entity: 'Юрлицо',
      packingList: 'Упаковочный лист',
      packingListReceived: 'Финальный лист получен — данные клиента',
      packingListEstimate: 'Не получен — наша оценка',
      notes: 'Примечания',
    },

    legs: {
      carriesExpenses:
        'Направление нельзя удалить, пока по нему учтены расходы: {{items}}. Сначала перенесите или удалите их.',
      add: 'Добавить направление',
      edit: 'Изменить направление',
      /* Доступное имя карточки: она теперь ССЫЛКА на страницу направления, а
         не блок, открывающий форму. Называет и номер, и сам маршрут — в списке
         из четырёх карточек «Открыть направление» четыре раза подряд не
         говорит, какое из них открывают. */
      open: 'Открыть направление {{n}}: {{from}} → {{to}}',
      carrier: 'Перевозчик',
      /* Выбор перевозчика из справочника (FR-40). Предлагаются два вида из
         семи — контрагент, который везёт, и владелец транспорта, — и вид
         подписан в строке: это разные отношения, а одна и та же компания
         иногда и то и другое. */
      carrierPlaceholder: 'Выберите перевозчика…',
      carrierSearch: 'Название перевозчика…',
      /* Для сохранённого перевозчика, которого нет в справочнике: имя из
         накладной, вписанное до того, как поле стало выбором. Остаётся
         выбранным — запись не ошибочна, она старше элемента управления. */
      carrierAsEntered: 'как введено',
      /* Для связанного перевозчика, которого потом отключили в справочнике.
         Тоже остаётся выбранным: перевозка состоялась. */
      carrierInactive: 'отключён',
      /* Запасной выход внутри списка: перевозчика, который сейчас перед
         глазами, в справочнике чаще всего ещё нет, и если заставить выйти из
         направления ради его заведения, направление сохранят вообще без
         перевозчика. */
      carrierAdd: 'Добавить контрагента',
      reference: 'Номер документа',
      /* Страна рядом с каждой точкой маршрута (FR-19). Само МЕСТО остаётся
         прежним — терминал, разъезд, площадка, — а это второй, структурный
         факт, по которому группируется отчёт по коридору. */
      originCountry: 'Страна отправления',
      destinationCountry: 'Страна назначения',
      /* Сводная строка секции: «2 направления · ж/д + авто через Хоргос». */
      count_one: '{{count}} направление',
      count_few: '{{count}} направления',
      count_many: '{{count}} направлений',
      legN: 'Направление {{n}}',
      /* Документ направления — словами отрасли: CMR на авто, накладная на
         остальных. */
      waybill: 'Накладная',
      cmr: 'CMR',
      notIssued: 'не выписана',
      /* Транспортное средство рейса, подписано по виду транспорта (FR-26).
         Одна подпись «рейс / номер рейса» заставляла бы диспетчера каждый раз
         соображать, какая половина про него; карточка и форма вид уже знают. */
      flightNumber: 'Рейс',
      voyageNumber: 'Рейс судна',
      vehicles: 'т/с',
      trucks: 'Фуры',
      /* Госномера тягачей (FR-67). Их на одном направлении может быть
         несколько: партия, идущая двумя машинами, — это одно направление и два
         т/с. */
      addPlate: 'Добавить машину',
      removePlate: 'Убрать машину',
      plateN: 'Госномер {{n}}',
      platePlaceholder: 'TM-AH 4471',
      /* Контейнеры парами КНТР/пломба (FR-67). */
      containers: 'Контейнеры',
      addContainer: 'Добавить контейнер',
      removeContainer: 'Убрать контейнер',
      containerN: 'Контейнер {{n}}',
      containerPlaceholder: 'MSKU 0419871',
      sealN: 'Пломба {{n}}',
      sealPlaceholder: 'SL-88421',
      containerTypeN: 'Тип {{n}}',
      containerTypePlaceholder: '40HC',
      /* Незапломбированный контейнер так и пишется. Пустое значение означает
         «ещё не опломбирован» или «неизвестно» — но не «без пломбы», и пробел
         рядом с номером читался бы как контейнер, который пломбировать не
         требовалось. */
      sealNotRecorded: 'пломба не указана',
      datePlanned: '{{date}} · план',
      /* Опоздание против ETA, рядом с фактом: «20 июл (+1 д)». */
      delay: '(+{{count}} д)',
      /* Сколько направление идёт по плану. Приходит с сервера
         (casefile.Leg.TransitDays), а не вычитается здесь: два правила о том,
         какой конец считается, дают два ответа на одном экране. */
      duration: 'Длительность',
      days_one: '{{count}} день',
      days_few: '{{count}} дня',
      days_many: '{{count}} дней',
      /* Ноль дней — это настоящий срок, а не отсутствующий: машина между двумя
         городами грузится и разгружается за утро. Словами, потому что «0 дней»
         читается как направление, которому не проставили даты. */
      sameDay: 'в тот же день',
      /* Срок всего маршрута: первое отправление — последнее прибытие. Без
         счётчика дней рядом: дни считает сервер на каждом направлении, а сумма,
         сложенная здесь, была бы вторым правилом про промежутки между ними. */
      span: '{{from}} — {{to}}',
      spanFrom: 'с {{date}}',
      /* Отметка на направлении, где груз сейчас. Какое оно — решает сервер
         (`legnow` в casefile); здесь оно только называется. */
      currentLeg: 'Текущее направление',
      expenses: 'Затраты',
      plannedExpense: 'Расчётные затраты',
      /* Что уже ушло перевозчику по этому направлению и сколько осталось
         (issue #35, история 6). Считается сервером и складывается на карточку
         из того же чтения денег, что и на странице направления, — две цифры об
         одном долге не разойдутся. */
      paidToCarrier: 'Оплачено',
      carrierOutstanding: 'Остаток перевозчику',
      /* Таблица фур на странице направления (FR-67, история 6). Направление
         обычно заказывают шестью фурами за один звонок: полоса сверху
         подставляет общие данные в новые строки, но у каждой фуры может быть
         свой перевозчик и своя ставка. */
      vehiclesTable: {
        planArticle: 'Плановая статья',
        mixedPlans: 'Разные статьи плана',
        threeRows: 'Добавить три строки',
        newTruck: 'Новая фура',
        /* Карандаш в строке. Назван по машине, а не по номеру строки: у трёх
           строк одна и та же иконка, и скринридер, читающий «Изменить,
           Изменить», не различает фуры. */
        editRowNamed: 'Изменить фуру {{plate}}',
        groupedColumns: {
          transport: 'Транспорт', people: 'Перевозчик и водитель', weight: 'Вес, кг',
          rate: 'Ставка', paid: 'Оплачено', actions: 'Действия',
        },
        title: 'Фуры и ставки',
        subtitle:
          'Общие данные заполняют новые строки, у каждой фуры может быть свой перевозчик и своя ставка',
        /* Значения, с которыми РОЖДАЮТСЯ новые строки. Добавление строки —
           панель под таблицей; эта кнопка только показывает поля. */
        sharedFields: 'Общие поля',
        oneMoreRow: 'Ещё строка',
        saveTrucks: 'Сохранить фуры',
        defaultCarrier: 'Перевозчик по умолчанию',
        defaultRate: 'Ставка по умолчанию',
        currency: 'Валюта',
        defaultsNote: 'подставляются в новые строки',
        /* Какую обобщённую строку квоты детализируют ставки этих фур (спека
           2026-09-01-expense-detail-grouping, L7). Без неё работа планирует
           одну и ту же перевозку дважды — по квоте и по фурам, — поэтому
           элемент назван по связи, а не по полю. */
        detailOfLabel: 'Детализирует строку плана',
        detailOfNone: 'не детализирует',
        detailOfFailed:
          'Фуры и ставки сохранены, но перепривязать строки плана к выбранной группе не удалось.',
        /* Открыть сохранённую строку на правку. Название действия, а не номер:
           кнопка ПОКАЗЫВАЕТ номер строки, а произносит то, что произойдёт. */
        editRow: 'Изменить строку {{n}}',
        columns: {
          plate: 'Машина',
          trailer: 'Прицеп',
          container: 'Контейнер',
          cmr: '№ CMR',
          carrier: 'Перевозчик',
          driver: 'Водитель',
          phone: 'Тел/WhatsApp',
          weight: 'Вес, кг',
          rate: 'Ставка',
          advance: 'Аванс',
          paid: 'Оплачено',
          pay: 'Оплата',
        },
        /* Действие на сохранённой строке. Названо по машине, а не одним видимым
           словом: строк с «Оплатить» несколько, и скринридер, слышащий
           «Оплатить, Оплатить», не знает, за какую фуру сейчас заплатит. */
        payRow: 'Оплатить',
        payRowNamed: 'Оплатить за {{plate}}',
        removeRowNamed: 'Удалить фуру {{plate}}',
        /* Чем ставка новой фуры записывается: одной плановой затратой в деньгах
           работы, названной по машине, за которую договорились. */
        rateItem: 'Ставка фуры {{plate}}',
        newTrucksNote_one:
          '{{count}} новая фура · ставка запишется как плановая затрата направления',
        newTrucksNote_few:
          '{{count}} новые фуры · ставки запишутся как плановые затраты направления',
        newTrucksNote_many:
          '{{count}} новых фур · ставки запишутся как плановые затраты направления',
        /* Прошла ПОЛОВИНА сохранения, и фраза говорит какая. Более общая
           формулировка заставила бы заново вбивать шесть уже сохранённых
           фур. */
        savedTrucksRatesFailed_one: 'Фуры сохранены, {{count}} ставка не записана.',
        savedTrucksRatesFailed_few: 'Фуры сохранены, {{count}} ставки не записаны.',
        savedTrucksRatesFailed_many: 'Фуры сохранены, {{count}} ставок не записано.',
        retryRates: 'Записать ставки',
        /* Две величины, которые в строке можно ввести неверно. Раньше их молча
           отбрасывали: ставка не числом сохраняла фуру вообще без ставки, а вес
           не числом СТИРАЛ уже записанные килограммы — и всё это под успешным
           сохранением. */
        rateInvalid_one: '{{count}} ставка не число — например 1200 или 1 200,50.',
        rateInvalid_few: '{{count}} ставки не числа — например 1200 или 1 200,50.',
        rateInvalid_many: '{{count}} ставок не числа — например 1200 или 1 200,50.',
        weightInvalid_one: '{{count}} вес не в целых килограммах — только цифры.',
        weightInvalid_few: '{{count}} веса не в целых килограммах — только цифры.',
        weightInvalid_many: '{{count}} весов не в целых килограммах — только цифры.',
      },
      /* Почему сохранение направления недоступно. Запись заменяет НАПРАВЛЕНИЕ
         ЦЕЛИКОМ вместе со строками фур, поэтому тот, кому перевозчик и водитель
         не показаны (AD-6), отправил бы их пустыми и стёр бы. Одна фраза на обе
         формы — карточку правки и таблицу фур: они шлют один и тот же PUT, и два
         разных объяснения одного отказа расходятся первым же исправлением.
         Сказано заранее, а не после нажатия, которое молча уничтожило бы
         данные. */
      identityHidden:
        'Перевозчик и водитель по этому направлению вашей роли не показаны, а сохранение их очистило бы. Попросите внести изменения того, у кого есть коммерческий доступ.',
      /* Здесь был `createdTemplateFailed` и ушёл вместе с поверхностью, которой
         он был нужен: создание больше не держит читателя на форме, когда отказало
         только применение шаблона. Оно ведёт на существующее направление, где над
         пустой цепочкой стоит отказ, а рядом — «Применить шаблон»: обычное
         состояние плеча без коридора плюс одна фраза, а не отдельный режим
         со своим словарём. */
      /* Где направление видели последний раз (FR-24). Сервер передавал это с историей
         3.4, и ни один компонент не выводил. */
      lastLocation: 'Последнее местоположение',
      reportedAt: 'сообщено {{at}}',
      /* Удаление направления. Отказ здесь и есть контроль (FR-67):
         направление с затратами удалить нельзя, и сервер называет эти
         затраты. */
      delete: 'Удалить направление',
      deleteConfirm:
        'Удалить направление? Вместе с ним уйдут его даты, точки и контейнеры.',
      deleteConfirmAction: 'Удалить направление',
      emptyTitle: 'Направлений пока нет',
      emptyDescription:
        'Добавьте направления перевозки, чтобы отслеживать её от отправления до доставки.',
    },

    /* Страница одного направления (issue #35, история 3).
     *
     * Направление стало отдельным адресом, потому что у него появилась ЦЕПОЧКА
     * ТОЧЕК: где груз сейчас, сколько дней положено на каждый переход и когда
     * ожидается следующая точка. Цепочку читают поперёк, а не вниз по колонке
     * шириной в модальное окно.
     *
     * Слова здесь держат два факта раздельно. ЦЕПОЧКА — это позиция груза,
     * СТАТУС — это отчёт о движении: направление может стоять «на границе»,
     * когда последняя пройденная точка — Алматы, и одно слово на оба вопроса
     * половину времени врало бы. */
    legPage: {
      allPoints: 'Все {{count}} точек',
      nextPoint: 'Следующая точка',
      routeEnd: 'Конец маршрута',
      positionAutomatic: 'Положение рассчитано автоматически',
      positionReported: 'Прибытие отмечено оператором',
      positionUnknown: 'Положение ещё не отмечено. Откройте точки маршрута, чтобы отметить прибытие.',
      dateLabels: { etd: 'План отправления', eta: 'План прибытия', atd: 'Факт отправления', ata: 'Факт прибытия' },
      /* Путь назад ведёт в тот же раздел, из которого пришли: обе карточки,
         открывающие направление, живут там, и «Обзор» заставил бы искать
         список заново. */
      back: 'назад к работе',
      /* Нейтральная метка: у цепочки есть нормативы, значит позиция
         передвинется сама (история 4). Это ФАКТ об этом направлении, а не
         настройка: цепочка, набранная руками без сроков, никуда не поедет, и
         метка на ней обещала бы движение, которого не будет. */
      autoTag: 'Авто',
      /* Подзаголовок-строка: кто везёт, по какой бумаге, когда выехал и когда
         ждём. Пустые части не печатаются — «Перевозчик — · — · —» это четыре
         разделителя ни о чём. */
      departs: 'выезд {{date}}',
      departed: 'выехал {{date}}',
      arrives: 'ожидаемое прибытие {{date}}',
      arrived: 'прибыл {{date}}',

      routeTitle: 'Маршрут',
      routeSubtitle:
        'Точки маршрута с нормативами перехода. Позиция груза передвигается по ним автоматически; нажмите на точку, чтобы отметить фактическое прибытие, когда оно известно раньше.',
      routeEmpty:
        'У направления пока нет точек. Примените шаблон или задайте маршрут вручную.',
      /* Нажатие на кружок. Две подписи, потому что это два разных действия, и
         обе называют точку: кружок сам по себе ничего не говорит скринридеру, а
         «Отметить» без названия не сказало бы, какую именно. Название НЕ
         склоняется — произвольный топоним из справочника просклонять нельзя, —
         поэтому оно в кавычках и в именительном падеже. */
      markThrough: 'Отметить прибытие по «{{point}}» включительно',
      clearFrom: 'Снять отметку с «{{point}}» и со всех точек после неё',
      /* Отказ, а не запрет по правам: у отправленного плеча сервер сам
         возвращает отметку в точку отправления (stampDeparture), так что снятие
         отменило бы само себя. Молча ничего не делающий контрол читается как
         сломанная система, поэтому причина названа. */
      departureHeld:
        'Плечо отправлено: отметку в точке отправления снимает смена статуса, а не этот экран.',
      /* Норматив перехода под отрезком между точками. Без форм множественного
         числа: «д.» не склоняется, как и «(+{{count}} д)» у опоздания выше. */
      normDays: '{{count}} д.',
      /* Чьим словом отмечено прибытие. Это разная степень уверенности в том,
         где груз: «факт» — сообщил человек, «авто» — предположила система по
         нормативу. Одно слово на оба случая позволило бы прочитать
         предположение как подтверждение. */
      pointReachedFact: 'факт',
      pointReachedAuto: 'авто',
      pointExpected: 'ожид. {{date}}',
      /* Только для скринридера: какая точка текущая, видно цветом и ореолом, а
         это должно пережить отсутствие цвета. */
      currentPoint: 'Текущая точка',
      /* Переход просрочен. Тон тихий: на коридоре с нормативом это ОБЫЧНОЕ
         событие — позиция передвинется сама, — а строка нужна ради поправки,
         которую может внести только человек. */
      overdueBanner_one:
        'Переход {{from}} → {{to}} просрочен на {{count}} день. Позиция передвинется автоматически; отметьте фактическое прибытие, если груз уже там.',
      overdueBanner_few:
        'Переход {{from}} → {{to}} просрочен на {{count}} дня. Позиция передвинется автоматически; отметьте фактическое прибытие, если груз уже там.',
      overdueBanner_many:
        'Переход {{from}} → {{to}} просрочен на {{count}} дней. Позиция передвинется автоматически; отметьте фактическое прибытие, если груз уже там.',
      /* Название точки НЕ склоняется — «Груз в Сарахс» было бы ошибкой, а
         просклонять произвольный топоним из справочника нельзя. Двоеточие
         оставляет название в именительном падеже и читается как подпись
         действия, а не как оборванная фраза. */
      cargoAtNext: 'Отметить прибытие: {{point}}',
      /* Отказ сервера, ради которого стоит писать отдельную фразу: путь к полю
         («points.2.reached») не подсказывает, что делать. */
      notContiguous:
        'Нельзя отметить прибытие в точку, пропустив предыдущую: отметьте сначала её.',

      /* Правка цепочки руками. Живёт рядом с «Применить шаблон» в шапке
         карточки: оба контрола пишут одну и ту же цепочку, только с разной
         ценой ввода. */
      routeEdit: 'Изменить маршрут',
      routeEditTitle: 'Правка маршрута',
      routeEditSubtitle:
        'Точки в порядке следования, с нормативом на каждый переход. Первая и последняя задают «Откуда» и «Куда» направления.',
      /* Предупреждение, а не ошибка: ничего не сломалось, это постоянное
         свойство сохранения. Отметки сервер переносит ПО ПОЗИЦИИ, а не по
         названию, поэтому перестановка строк оставляет отметку на месте. */
      routeEditArrivalsPinned:
        'Отметки о прибытии привязаны к позиции в цепочке, а не к названию точки: если переставить строки, отметка останется на своём месте в списке.',
      routeEditTooFew:
        'Маршрут из одной точки невозможен: в нём нет ни одного перехода. Добавьте вторую точку или удалите единственную.',
      routeEditTooMany: 'В маршруте не больше {{max}} точек.',
      routeEditSave: 'Сохранить маршрут',
      routeEditEndsHint: 'Концы направления подставятся из первой и последней точки',
      arrivalHeldHere: 'На этой позиции записано прибытие',
      movePointUp: 'Переместить точку {{n}} выше',
      movePointDown: 'Переместить точку {{n}} ниже',

      applyTemplate: 'Применить шаблон',
      applyTemplateConfirm: 'Применить',
      applyTemplateWarning:
        'Применение заменит текущие точки маршрута вместе с отметками о прибытии. Отменить это нельзя.',
      directionTemplate: 'Направление (шаблон)',
      directionTemplateHint:
        'Точки КОПИРУЮТСЯ в направление: правка справочника после этого перевозку не сдвинет.',
      directionTemplateNone: 'Без шаблона',

      /* Фуры переехали в `cases.legs.vehiclesTable` (история 6): там их можно
         править и там же их ставки. Здесь осталась фраза для направления, по
         которому машин ещё нет. */
      vehiclesTitle: 'Транспорт',
      vehiclesEmpty: 'Машины по этому направлению не записаны.',

      editTitle: 'Изменение направления',
      editSubtitle:
        'Сохранение заменяет запись целиком: очищенное поле именно очищается, а не остаётся прежним.',
      /* Фактические даты — теми же буквами, что и плановые рядом: это слова
         отрасли в обоих офисах. */
      atd: 'ATD',
      ata: 'ATA',
      stallNote: 'Причина задержки',
      lastLocationHint: 'Откуда пришло последнее сообщение о грузе.',
    },

    /* Добавление направления — отдельная страница `/cases/:caseId/legs/new`.
     *
     * Создание ушло из модального окна по той же причине, что и сама запись:
     * направление — это страница, а форма, которую заполняли в окне и после
     * сохранения открывали как страницу, была последним местом, где это
     * противоречие ещё жило. Слова здесь делают ту же работу, что и в окне:
     * говорят, где спрашивают всё остальное, чтобы никто не искал на этой форме
     * цепочку точек, фуры или деньги. */
    legNew: {
      title: 'Новое направление',
      /* Сказано, куда читатель попадёт ДАЛЬШЕ: в этом и причина, почему форма
         короткая — всё, чего она не спрашивает, спрашивают на странице, куда
         ведёт удачное сохранение. */
      subtitle:
        'Откуда, куда и чем везут. Точки маршрута, фуры и деньги спрашивают на странице самого направления — туда и ведёт сохранение.',
      formSubtitle: 'Обязательны два конца и вид транспорта; остальное подождёт.',
      create: 'Создать направление',
      /* Почему концы плеча показаны, а не спрошены: применение коридора вызывает
         SetLegEnds в той же транзакции, так что напечатанное здесь было бы
         затёрто ровно этими значениями секундой позже. Форма спрашивала бы то,
         чей ответ выбрасывает. */
      endsFromTemplate: 'Из выбранного направления',
      /* Разделы, которые запись вот-вот получит. Названы, а не спрятаны: тогда
         страница, куда читатель попадёт после «Создать», узнаётся как та же
         самая. */
      routeLater:
        'Точки коридора лягут сюда, как только направление появится; дальше их можно править и отмечать по мере прохождения груза.',
      vehiclesLater:
        'Номера, водители и ставки, согласованные с перевозчиками, — после создания.',
      moneyLater: 'Согласовано, оплачено и остаток — после создания.',
    },

    /* Деньги одного направления (issue #35, история 6).
     *
     * Блок отдаётся ВСЕМ, кто дотягивается до работы, а имя перевозчика внутри
     * него скрывается по полям — поэтому здесь нигде не написано «вашей роли не
     * показано» про цифру. Разделение «аванс / оплачено» — ответ сервера по
     * правилу ATA, и формулировки нарочно не выдают его за то, что посчитал
     * экран. */
    legFinance: {
      title: 'Оплаты перевозчику',
      subtitle: 'Аванс и оплаты по направлению считаются из проведённых платежей',
      payCarrier: 'Записать оплату',
      agreed: 'Согласовано',
      advance: 'Аванс',
      paidTotal: 'Оплачено всего',
      outstanding: 'Остаток',
      /* Подписи под плитками: по чему считается каждая цифра. */
      trucks_one: '{{count}} фура',
      trucks_few: '{{count}} фуры',
      trucks_many: '{{count}} фур',
      beforeArrival: 'до прибытия',
      paymentCount_one: '{{count}} платёж',
      paymentCount_few: '{{count}} платежа',
      paymentCount_many: '{{count}} платежей',
      perVehicle_one: 'по {{count}} фуре',
      perVehicle_few: 'по {{count}} фурам',
      perVehicle_many: 'по {{count}} фурам',
      /* Нейтральная метка на оплате, прошедшей до прибытия. Это ФАКТ СЕРВЕРА:
         таблица его выводит и никогда не выводит заново из дат рядом. */
      advanceTag: 'аванс',
      /* У записи ещё не было второй пары глаз (0059, L3). Про ДЕНЬГИ это не
         говорит ничего: неподтверждённая оплата входит во все цифры карточки
         наравне с подтверждённой (L4) — поэтому «не подтверждён» про запись, а
         не «не оплачено». */
      pendingTag: 'не подтверждён',
      columns: {
        date: 'Дата',
        item: 'Назначение',
        vehicle: 'Фура',
        method: 'Способ',
        amount: 'Сумма',
        decisions: 'Действия',
      },
      noSettlements: 'По этому направлению пока ничего не оплачено.',
      /* Список по фурам и действие на каждой строке. Он заменил «сначала
         проведите факт»: ставка без счёта больше не шаг, который нужно пройти
         до оплаты, — оплата признаёт долг тем же запросом. */

      /* «Оплатить перевозчику» — одно окно на долг, платёж и привязку.
         Формулировки нигде не обещают, что цифры кого-то ждут: платёж входит в
         суммы сразу, подтверждение — проверка записи (L4). */
      pay: {
        title: 'Оплатить перевозчику',
        submit: 'Оплатить',
        atomicNote:
          'Затрата, платёж и привязка записываются вместе. Финансы подтверждают запись потом — деньги считаются сразу.',
        noVehicles: 'На направлении пока нет фур — платить не за что.',
        vehicle: 'Фура',
        vehicleHint: 'За какую фуру уходят деньги. От неё зависит долг ниже.',
        vehiclePlaceholder: 'Выберите фуру',
        vehiclePosition: 'фура {{position}}',
        chooseVehicleFirst: 'Выберите фуру, чтобы увидеть долг по ней.',

        debtHeading: 'Долг',
        /* Затрата уже проведена. Показана, но не редактируется: правка
           существующей цифры — действие таблицы затрат, а второй способ её
           поменять — второй ответ на вопрос, сколько выставил перевозчик. */
        existingActual: 'Уже проведено: {{item}} — {{amount}}',
        chooseLine: 'Какая затрата',
        chooseLineHint: 'У этой фуры несколько проведённых затрат. Ничего не выбрано за вас.',
        debtAmount: 'Сумма долга',
        debtFromPlan:
          'Ставка по договорённости — {{amount}}. Измените, если перевозчик выставил другую сумму.',
        debtUnplanned:
          'По этой фуре ставки не было — укажите, сколько должны. Затрата проведётся как внеплановая.',
        debtComment: 'Причина',
        debtCommentHint: 'Необязательно. Останется при затрате, никому не отправляется.',
        debtCommentRequired:
          'Это больше согласованных {{amount}} — напишите почему. Причина останется при затрате.',

        paymentHeading: 'Платёж',
      },
      unavailableTitle: 'Не удалось прочитать деньги по этому направлению',
      unavailableDescription:
        'Ничего не потеряно. Попробуйте снова — если не проходит, возможно, нет курса на дату одной из сумм.',
    },

    team: {
      title: 'Участники',
      /* Раздел теперь стоит сам по себе, а не карточкой сбоку от «Обзора», и
         должен сказать, что добавление ДЕЛАЕТ: членство и есть доступ (AD-5,
         FR-56), а не оформление. Формулировка та же, что у котировки. */
      hint: 'Коллеги, которые ведут эту работу. Добавленный увидит её со следующего запроса.',
      assign: 'Назначить коллегу',
      colleague: 'Коллега',
      assignHint:
        'Работа станет ему видна со следующего запроса. Отключённых учётных записей в списке нет.',
      owner: 'Ответственный',
      remove: 'Убрать {{name}} из команды',
    },
    /* Стороны сделки, а не сотрудники: см. комментарий у `tabs`. Ключ остался
       `participants` — так называются таблица, эндпоинт и DTO, и переименовывать
       их ради подписи значило бы менять контракт из-за слова на экране. */
    participants: {
      title: 'Контрагенты',
      add: 'Добавить контрагента',
      edit: 'Изменить контрагента',
      role: 'Роль',
      notifyByEmail: 'Уведомлять по email',
      contactHint: 'Используется для уведомлений этому контрагенту.',
      /* Галочка стоит по умолчанию (FR-48), поэтому форма должна уметь
         попросить адрес, без которого она ничего не значит. Формулировка та же,
         что и в котировке: одно правило, сказанное двумя разными фразами,
         читается как два разных правила. */
      contactNeededForEmail: 'Укажите email, чтобы уведомлять этого контрагента.',
      /* Заголовок раздела: количество и правило доставки одной строкой. */
      count_one: '{{count}} контрагент',
      count_few: '{{count}} контрагента',
      count_many: '{{count}} контрагентов',
      externalNote: 'внешние стороны получают уведомления только по email',
      notifiedByEmail: 'Уведомлён по email',
      noContact: 'Контакт не указан',
      emptyTitle: 'Контрагентов пока нет',
      emptyDescription:
        'Здесь появятся грузоотправители, грузополучатели, агенты и водители по этой работе.',
      delete: 'Убрать контрагента',
      deleteConfirm: 'Убрать этого контрагента из работы?',
      deleteConfirmAction: 'Убрать контрагента',
    },

    money: {
      /* `forbiddenTitle` и `forbiddenDescription` УДАЛЕНЫ, а не перенесены. Они
         описывали состояние, которого сервер не может выдать: по AD-5 деньги
         работы доступны её команде, а тот, кто до работы не дотягивается,
         получает 404 на саму работу. На деле они выводились для работы без
         ставки — состояния, с которого начинается каждая работа, созданная
         напрямую, и все 241 перенесённых. */
      /* Блока нет вовсе: не удалось прочитать данные или курс под ними. Называет
         и препятствие, и причину, и никогда не извиняется. */
      unavailableTitle: 'Не удалось прочитать деньги по этой работе',
      unavailableDescription:
        'Ничего не потеряно. Попробуйте снова — если не проходит, возможно, нет курса на дату одной из сумм.',
      /* Ещё ничего не внесено — это не то же самое, что «не показываем».
         Говорит, для чего раздел, и предлагает действие (UX-DR-17). */
      untouchedTitle: 'По этой работе пока ничего не внесено',
      untouchedDescription:
        'Внесённые затраты появятся здесь рядом с расчётом. Согласованную ставку выставляют «Финансы» в самой работе.',
      title: 'Деньги · расчёт и факт',
      /* Подзаголовок называет правило всей карточки (FR-36): расчёт пришёл из
         принятой котировки, ставка меняется только через согласование. */
      subtitleFromQuote:
        'Статьи расчёта перенесены из котировки {{number}} · ставка зафиксирована, изменения — через согласование',
      subtitle: 'Ставка зафиксирована — изменения через согласование',
      recordActual: 'Внести фактическую затрату',
      addExpense: 'Добавить затрату',
      editExpense: 'Изменить затрату',
      /* Статья, которую эта форма править не может. У расчёта, закрытого
         НЕСКОЛЬКИМИ счетами (AD-4), нет одной фактической суммы — свёрнутой
         форме нечего держать, и сервер отклоняет такое сохранение (409). Текст
         называет и статью, и число фактов: «нельзя изменить» без того и другого
         читается как поломка, а не как факт про эту конкретную статью. */
      severalActualsTitle: 'Закрыта несколькими счетами',
      severalActualsDescription:
        'По статье «{{description}}» к одному расчёту отнесено фактов: {{lines}} — одной фактической суммы для правки здесь нет. Изменяйте счета там, где они были внесены.',
      /* Удаление статьи убирает СТАТЬЮ целиком: и расчёт, и все факты по нему,
         в одной транзакции. Раньше удалялся только факт, а расчёт оставался на
         экране, будто ничего не удалили. */
      deleteExpense: 'Удалить статью',
      /* Две иконки в самой строке. Названы ПО СТАТЬЕ, а не только по
         действию: в таблице из восьми затрат восемь одинаковых карандашей,
         и скринридер, читающий «Изменить, Изменить», не различает строки. */
      editExpenseNamed: 'Изменить «{{line}}»',
      deleteExpenseNamed: 'Удалить «{{line}}»',
      deleteExpenseConfirm: 'Удалить эту статью затрат?',
      deleteExpenseConfirmAction: 'Удалить статью',
      settled: 'Оплачено',
      actualHint: 'Оставьте пустым, пока затрата не понесена. Отклонение считается само.',
      totals: 'Итого',
      /* Под строкой, которую она объясняет, а не в баннере над карточкой —
         сама строка держит жёлтую заливку, пока Суперадмин решает. */
      passThroughPending: 'Перевыставляется клиенту, ждёт согласования',
      expensesEmptyTitle: 'Статей затрат пока нет',
      expensesEmptyDescription:
        'Статьи расчёта переносятся из котировки; факт вносится по мере поступления счетов.',
      clientReceivable: 'К получению от клиента',
      /* Из чего складывается сумма к получению (FR-32, FR-33). `tranchePaid`
         ушёл вместе с `ReceivableTranche.settled`: чтобы сказать, оплачен ли
         отдельный транш, нужны распределения именно по нему, а блок отдаёт лишь
         общую сумму к получению — различие, которого клиент не может посчитать,
         он не должен и рисовать. */
      trancheExpected: '№{{n}} {{amount}} — ожидается {{date}}',
      trancheUndated: '№{{n}} {{amount}} — дата не согласована',
      /* Полоса с датами. Транш без даты не просрочен: вторая половина 50-50
         создаётся без даты и ждёт доставки. */
      tranchesTitle: 'Ожидаемые поступления',
      trancheN: 'Транш {{n}}',
      trancheDue: 'Ожидается',
      /* Собственное имя пикера, и оно называет ТРАНШ: у работы 50-50 их два
         подряд, а серия, прочитанная как «Ожидается, Ожидается», — серия, с
         которой ничего нельзя сделать. */
      trancheDueNamed: 'Ожидаемая дата транша {{n}}',
      /* Две колонки, по которым выравнивается серия. Сказаны один раз над
         колонкой, а не в каждой строке — см. заметку в TrancheStrip. */
      trancheColumns: { tranche: 'Транш', share: 'Доля', amount: 'Сумма' },
      /* Имя самой строки. Номер — это СМЫСЛ серии («второй транш» — то, что
         спрашивают в работе), а общее число рядом превращает «1» в половину,
         а не в целое. */
      trancheOfSeries: 'Транш {{n}} из {{total}}',
      payables: 'К оплате контрагентам',
      dueOn: 'срок {{date}}',
      pendingApprovalLabel: 'Ждёт согласования',
      pendingNote: 'Доплата клиента · ждёт Суперадмина',
      noPending: 'Ничего не ждёт согласования',

      /* Три показателя FR-30. Три отдельных названия, а не одно «прибыль»:
         в исходной таблице слово было одно на все три, и именно из-за этого
         два человека называли по одной перевозке разную маржу. */
      expectedProfit: 'Ожидаемая прибыль',
      expectedProfitNote: 'Согласованная ставка минус плановые затраты',
      forecastProfitLabel: 'Прогноз прибыли',
      forecastProfitNote: 'Затраты по факту плюс то, что ещё в плане',
      finalProfit: 'Итоговая прибыль',
      finalProfitNote: 'Полученные деньги минус выплаченные, по курсам расчётов',
      noRateYet: 'Ставка ещё не согласована — здесь только плановые затраты',

      payments: {
        heading: 'Платежи по перевозке',
        emptyTitle: 'Платежей пока нет',
        emptyDescription:
          'Приход и расход вносятся на странице «Деньги» и появляются здесь вместе с тем, что они закрывают.',
        unallocated: '{{amount}} ещё не распределено',
        /* Пять фактов о платеже, по колонке на каждый. «Комиссия» — своя
           колонка и никогда не часть «Суммы»: сумма — то, что ЗАКРЫЛОСЬ,
           комиссия — во сколько обошёлся перевод, и складывать их нельзя
           нигде (AD-11). */
        columns: {
          payment: 'Платёж',
          fee: 'Комиссия',
          unallocated: 'Не распределено',
          amount: 'Сумма',
          date: 'Получен',
        },
      },

      columns: {
        description: 'Статья',
        counterparty: 'Контрагент',
        planned: 'Расчёт',
        actual: 'Факт',
        line: 'Статья затрат',
        vendor: 'Контрагент',
        leg: 'Направление',
        plannedIn: 'Расчёт {{currency}}',
        actualIn: 'Факт {{currency}}',
        variance: 'Отклонение',
        actions: 'Действия',
      },
      /* За что заплатили, ссылкой на страницу направления (FR-67, история 6).
         Номер фуры идёт рядом, когда строка её называет: «какое направление» и
         «какая фура» — один вопрос на рабочем месте. */
      legLink: 'Направление {{n}}',
      legLinkVehicle: 'Направление {{n}} · {{plate}}',
      /* Затрата, которая не относится ни к одному движению, — оформление,
         банковская комиссия. Это законно и часто, поэтому сказано словами:
         пустая клетка читалась бы как строка, которую забыли заполнить. */
      unattributed: 'не привязан',
      /* План, детализированный по фурам (спека
         2026-09-01-expense-detail-grouping). Собственная цифра родителя в
         итогах больше не считается, поэтому она сказана ЗДЕСЬ — как бенчмарк,
         которым стала: что заложила квота и насколько от неё ушли реально
         законтрактованные ставки. При разных валютах дельты нет — вычитать их
         друг из друга нельзя, — и подпись остаётся счётом строк. */
      groupNote: 'квота {{quota}} · {{delta}} · строк: {{count}}',
      groupNoteMixed: 'детальных строк: {{count}}',
      toggleGroup: 'Показать/скрыть детали {{line}}',
    },

    documents: {
      title: 'Счета и дебет-ноты',
      /* Отличие от «Файлов»: здесь то, что мы выставили сами, а не то, что
         прислали и приложили. */
      subtitle: 'Документы, выставленные по этой работе, и что по ним не закрыто.',
      raise: 'Новый документ',
      pick: {
        title: 'Выставить по расходам работы',
        description:
          'Отметьте записанные расходы, которые нужно выставить. Они станут позициями документа — их можно править, добавить свои или продолжить без единой и набрать вручную.',
        confirm: 'Продолжить',
        selected: 'Выбрано',
        count_one: '{{count}} позиция',
        count_few: '{{count}} позиции',
        count_many: '{{count}} позиций',
        count_other: '{{count}} позиций',
      },
      emptyTitle: 'Пока ничего не выставлено',
      emptyDescription:
        'Здесь появятся счета и дебет-ноты по этой работе. Новый документ создаётся черновиком — выставление это отдельный шаг.',
      /* Ссылка, а не кнопка «Изменить»: счёт правится на своей странице. */
      openInvoice: 'Открыть счёт',
    },

    /* Раздел «Счета» работы (он же `?section=documents`): согласованная ставка,
     * график платежей по ней и бумага, которая по этому графику ушла клиенту.
     *
     * Отдельная группа рядом с `documents`, а не внутри неё, потому что это два
     * разных вопроса на одном экране: «сколько ещё можно выставить» и «что уже
     * отправлено». Слово «работа» — как во всём финансовом словаре здесь. */
    invoices: {
      rateTitle: 'Ставка по котировке',
      rateSubtitle:
        'Сколько согласовано с клиентом, сколько уже выставлено и чем закрывается остаток.',
      fromQuote: 'по котировке {{quote}}',
      rate: 'Ставка',
      billed: 'Выставлено',
      paid: 'Оплачено',
      headroom: 'Можно выставить ещё',
      /* Цвет никогда не несёт смысл в одиночку: полоса — это украшение над
         цифрами, которые и так напечатаны рядом, а легенда называет каждую
         полосу словами. */
      legendPaid: 'оплачено клиентом',
      legendAwaiting: 'выставлено, ждём оплаты',
      legendFree: 'ещё не выставлено',
      /* Что именно внутри «выставлено». Сервер считает потолок по ВСЕМ
         позициям ВСЕХ счетов работы, включая черновики (`BilledOnCase`), и по
         этой же сумме откажет в следующей строке. Читатель, решивший, что
         считаются только выпущенные счета, увидит остаток больше того, что
         разрешит запись. */
      billedCountsDrafts:
        'В «выставлено» входят и черновики: потолок ставки сервер считает по всем позициям всех счетов работы и по этой же сумме откажет в следующей строке.',
      instalment: 'Платёж',
      expected: 'Ожидается',
      /* Транш без даты — не просрочка: вторую половину при схеме 50/50 заводят
         без даты нарочно, она ждёт даты поставки, которой пока никто не знает. */
      undated: 'дата не согласована',
      notBilled: 'счёт не выставлен',
      /* Выпущенный счёт снимает транш с учёта ЦЕЛИКОМ, на какую бы сумму он ни
         был выставлен: сервер спрашивает «есть ли выпущенный счёт на этот
         транш», а не «покрывает ли он его». Поэтому счёт на 500 против транша
         на 2 000 тихо убирает 1 500 из ожидаемых поступлений. Строка раньше
         показывала сумму транша рядом с номером такого счёта — единственное
         прочтение, которое заведомо неверно. */
      billedShort: 'выставлено {{billed}} из {{expected}} {{currency}} — остаток больше не ожидается',
      raiseInvoice: 'Выставить счёт',
      raising: 'Создаём…',
      noSchedule:
        'График платежей не заведён — ставка собирается одним счётом. Выставить можно и так: такой счёт просто не закрывает ни один транш.',
      /* Роли без коммерческого доступа денежный блок не приходит вовсе (AD-6).
         Это не «ставки нет» — это «мы не вправе показать», и путать их нельзя. */
      rateWithheldTitle: 'Деньги по этой работе не показываются',
      /* Ради этой фразы раздел и переделывали: расходы работы — это её
         себестоимость, и в счёте клиенту им делать нечего. */
      expensesNote:
        'Расходы работы на этот счёт не влияют: они складываются в себестоимость и перевыставляются дебет-нотой — отдельным документом на конкретного контрагента.',
    },

    files: {
      title: 'Файлы',
      upload: 'Загрузить',
      takePhoto: 'Фото',
      pending: 'В очереди',
      uploading: 'Отправка…',
      /* Загрузивший файл из очереди — тот, кто смотрит на экран: строки на
         сервере ещё нет, имя брать неоткуда. */
      you: 'Вы',
      /* Story 8.2, UX-DR-19. Четыре формы, а не две: в русском счётная форма
         зависит от последней цифры, и «3 файла» против «5 файлов» — это не
         стилистика, а грамматика. i18next выбирает по правилам плюрализации. */
      queuedTitle_one: '{{count}} файл ждёт отправки',
      queuedTitle_few: '{{count}} файла ждут отправки',
      queuedTitle_many: '{{count}} файлов ждут отправки',
      queuedTitle_other: '{{count}} файлов ждут отправки',
      queuedSending:
        'Сейчас отправляются. Можно уйти со страницы — они дойдут сами.',
      /* Здесь названа сама гарантия: файлы лежат на устройстве и уйдут сами.
         Это то обещание, из-за которого не придётся возвращаться и
         переснимать перегрузку. */
      queuedOffline:
        'Связи нет. Файлы сохранены на устройстве и уйдут сами, когда появится сигнал, — приложение можно закрыть.',
      sendNow: 'Отправить сейчас',
      dropHint:
        'Перетащите файлы сюда или нажмите «Загрузить». С телефона можно снять фото сразу.',
      uploadFailedTitle: 'Файл не отправился',
      /* Называет следующее действие, а не ошибку: с «PUT вернул 403» сделать
         нечего, а «добавьте файл снова» — можно. */
      uploadFailedBody:
        'По нему ничего не сохранено. Проверьте связь и добавьте файл ещё раз.',
      emptyTitle: 'Файлов пока нет',
      emptyDescription:
        'Здесь появятся накладные, упаковочные листы и фото перегрузки по этой работе.',

      /* Тип документа спрашивается один раз на всю выбранную пачку (0045).
         «Какой это документ» — вопрос про бумагу, а не про формат файла:
         сорок PDF, сгруппированных по «PDF», сгруппированы ни по чему. */
      kindTitle: 'Что это за документ',
      kind: 'Тип документа',
      kindHint:
        'По типу файлы группируются в списке — так нужную бумагу находят, не открывая каждую.',
      kindFieldHint: 'Не уверены — оставьте «Прочее», тип видно и его можно исправить.',
      kindSubmit_one: 'Добавить {{count}} файл',
      kindSubmit_few: 'Добавить {{count}} файла',
      kindSubmit_many: 'Добавить {{count}} файлов',

      /* Названия типов. Это ПОДПИСИ к кодам с сервера — на проводе едет
         'cmr', а не «CMR»: иначе группировка зависела бы от языка читателя, и
         два коллеги видели бы одну работу по-разному (AD-27). */
      kinds: {
        cmr: 'CMR',
        awb: 'Авианакладная',
        'rail-waybill': 'Ж/д накладная (СМГС)',
        'bill-of-lading': 'Коносамент',
        invoice: 'Инвойс',
        'packing-list': 'Упаковочный лист',
        'customs-declaration': 'Таможенная декларация',
        certificate: 'Сертификат',
        contract: 'Договор',
        photo: 'Фото',
        other: 'Прочее',
      },

      /* Удаление. Название файла подставляется в подпись кнопки, потому что
         строк в списке много, а «Удалить» на каждой — это кнопка, про которую
         с клавиатуры не понять, что она удалит. */
      delete: 'Удалить «{{name}}»',
      deleteConfirm: 'Удалить «{{name}}»? Файл исчезнет и из хранилища.',
      deleteConfirmAction: 'Удалить',
      deleteFailed: 'Файл не удалён',
      deleteFailedHint:
        'Он остался на месте. Проверьте связь и попробуйте ещё раз.',
    },

    /* История работы: ОДНА лента из двух источников — журнал действий и
       финансовый аудиторский след. Разделять их в тексте нечем и незачем:
       человек спрашивает «когда это изменилось и кто», а не «в какой из двух
       таблиц это лежит».

       Ключи `actions.*` буквально повторяют машинные ключи из
       `backend/cmd/asa/actions.go`, из которых снят префикс `case.`:
       "case.status.change" → `actions.status.change`. Совпадение буквальное
       НАМЕРЕННО — лента переводит ключ напрямую и при отсутствии перевода
       печатает сам ключ, поэтому маршрут, добавленный в actions.go завтра,
       портит одну строку, а не гасит раздел.

       Голос — тот же, что у `quotes.history.actions` ниже: короткое
       предложение в страдательном залоге о том, ЧТО произошло, а не о том, на
       что нажали. «Статус изменён», не «Пользователь изменил статус»: кто
       именно — лента печатает отдельной строкой, и повторять это в каждом
       заголовке значит писать имя дважды. */
    history: {
      title: 'История',
      count_one: '{{count}} событие',
      count_few: '{{count}} события',
      count_many: '{{count}} событий',
      /* Пусто — значит пусто по ОБОИМ источникам. Прежний текст обещал только
         финансовые изменения; с появлением журнала действий это перестало быть
         правдой, и обещание расширено до всего, что с работой делали. */
      emptyTitle: 'Пока ничего не происходило',
      emptyDescription:
        'Здесь появится всё, что делали с этой работой: действия команды и финансовые изменения — кто и когда.',
      /* Окна времени. «За всё время» выбрано по умолчанию: история одной
         работы — десятки записей, и сузить её это осознанный шаг, а не
         состояние, в котором экран открывается. Формулировки те же, что у
         периода в отчётах, — один и тот же выбор, сказанный двумя разными
         фразами, читается как два разных выбора. */
      filters: {
        all: 'За всё время',
        today: 'Сегодня',
        yesterday: 'Вчера',
        week: 'За неделю',
      },
      searchPlaceholder: 'Поиск по истории…',
      /* Отдельно от `emptyTitle` выше, и это разница по существу: у работы без
         истории снимать нечего, а здесь есть что — и текст называет, что
         именно снять. */
      noMatchTitle: 'Ничего не найдено',
      noMatchDescription:
        'Измените запрос или выберите «За всё время», чтобы снять фильтр по времени.',
      /* Снова отдельно от `emptyTitle` — и по той же причине, по которой
         скелет ждёт оба источника: если один отказал, а во втором ничего нет,
         «Пока ничего не происходило» это утверждение об истории, прочитанной
         наполовину. Неизвестно — не то же самое, что пусто. */
      unknownTitle: 'Историю не удалось прочитать целиком',
      unknownDescription:
        'Один из двух источников не ответил, а во втором записей нет — поэтому неизвестно, происходило ли с этой работой что-нибудь. Повторите загрузку выше.',
      /* Отказ ОДНОГО источника не гасит раздел: второй показывается как есть,
         а плашка называет, чего в ленте не хватает. Молчаливая половина
         истории читается как вся история — и именно так рождается «этого
         никто не менял». */
      loadFailed: {
        actions: 'Журнал действий не загрузился — в ленте пока только финансовые изменения.',
        audit: 'Финансовые изменения не загрузились — в ленте пока только действия команды.',
        retry: 'Повторить',
      },
      /* Лента не листается, и предел есть у обоих источников: у следа — тот,
         который запрашивает клиент, у журнала действий — тот, который сервер
         накладывает сам, потому что параметра предела у этого чтения нет.
         Когда предел достигнут, об этом говорят вслух — обрезанная лента,
         которая молчит, выглядит как полная. Фраза у каждого источника своя:
         достигнутый предел одного ничего не говорит о другом. Числа
         подставляются, а не написаны здесь: иначе рост предела молча оставил
         бы во фразе старое. */
      truncated: {
        actions: 'Показаны последние {{limit}} записанных действий.',
        audit: 'Показаны последние {{limit}} финансовых изменений.',
      },
      audit: {
        reason: 'Причина',
      },
      actions: {
        create: 'Работа создана',
        update: 'Данные работы изменены',
        status: {
          change: 'Статус изменён',
        },
        reopen: 'Работа открыта заново',
        leg: {
          add: 'Добавлено направление',
          update: 'Направление изменено',
          remove: 'Направление удалено',
        },
        /* Контрагенты — внешние стороны перевозки; команда ниже — сотрудники
           ASA. Пара стоит рядом, потому что путаются именно эти два, и
           различие несут сами слова, а не порядок ключей. */
        participant: {
          add: 'Добавлен контрагент',
          update: 'Контрагент изменён',
          remove: 'Контрагент убран из работы',
        },
        team: {
          add: 'Сотрудник добавлен в команду',
          remove: 'Сотрудник выведен из команды',
        },
        /* `remove` в ленте работы не появится: удаление файла пишется в журнал
           под сущностью "file", а не "case". Перевод стоит здесь всё равно —
           он стоит одну строку и избавляет следующего читателя от вывода,
           будто словарь неполон. То же ниже у `status-update.send-bulk`. */
        file: {
          attach: 'Файл прикреплён',
          remove: 'Файл удалён',
        },
        /* Два ключа, один текст, и это не копипаста. Обе записи означают одно:
           в переписку по работе что-то написали. Придумывать им разные
           заголовки значит показать читателю различие, по которому он всё
           равно ничего не сделает. */
        message: {
          post: 'Сообщение отправлено в чат',
        },
        chat: {
          post: 'Сообщение отправлено в чат',
        },
        /* Массовая отправка называет свои работы в теле запроса, а не в пути,
           поэтому запись остаётся без id работы и ни в одну ленту не
           попадает. Переведена по той же причине, что и `file.remove`. */
        'status-update': {
          send: 'Клиенту отправлен статус',
          'send-bulk': 'Статус отправлен по нескольким работам',
        },
        rate: {
          set: 'Ставка установлена',
        },
        expense: {
          record: 'Добавлена статья затрат',
          update: 'Статья затрат изменена',
          delete: 'Статья затрат удалена',
        },
        payment: {
          record: 'Внесён платёж',
        },
        invoice: {
          raise: 'Создан черновик счёта',
          update: 'Счёт изменён',
          line: {
            add: 'В счёт добавлена позиция',
            update: 'Позиция счёта изменена',
            delete: 'Позиция счёта удалена',
          },
          render: 'По счёту сформирован документ',
        },
        tranche: {
          date: {
            set: 'Назначена дата транша',
          },
        },
      },
    },

    chat: {
      label: 'Чат по работе',
      placeholder: 'Написать команде по работе',
      send: 'Отправить сообщение',
      heading: 'Чат',
      internalOnly: 'Только для своих',
      you: 'Вы',
      sending: 'Отправляется…',
      latest: 'К последним',
      /* Story 8.3. Переписка грузится страницами и опрашивается, пока панель
         на экране, — эти строки описывают состояния, которых у витринной
         ленты не было: первая страница в пути, догрузка истории и сообщения,
         пришедшие, пока человек читал выше. */
      loading: 'Загружаем переписку',
      failed: 'Не удалось загрузить переписку.',
      empty: 'Пока ничего не написали. Начните разговор.',
      earlier: 'Что было раньше',
      loadingEarlier: 'Загружаем…',
      /* Плашка «вниз» после того, как что-то пришло ниже читателя. Счёт назван
         прямо: вернуться к одному сообщению и к трём — разные дела. */
      unread_one: '{{count}} новое сообщение',
      unread_few: '{{count}} новых сообщения',
      unread_many: '{{count}} новых сообщений',
      /* Имя кнопки в шапке на телефоне для скринридера. Заменяет обычную
         подпись, а не дополняет её, — счёт должен звучать как часть того, зачем
         кнопка, а не как довесок в конце. */
      openWithUnread_one: 'Чат по работе, {{count}} новое сообщение',
      openWithUnread_few: 'Чат по работе, {{count}} новых сообщения',
      openWithUnread_many: 'Чат по работе, {{count}} новых сообщений',
      messageLabel: 'Сообщение',
      sendHint: 'Enter — отправить · Shift + Enter — новая строка',
      attach: 'Прикрепить файлы',
      removeAttachment: 'Убрать {{name}}',
      dropHere: 'Отпустите, чтобы прикрепить',
      tooLarge: '{{name}} больше 50 МБ — файл не прикреплён.',
      tooMany: 'К сообщению можно приложить {{count}} файлов.',
      /* Разделители дней между сообщениями. Сначала два названных дня, потом
         дата: «сегодня» и «вчера» — это то, с чем читатель сверяет рабочую
         переписку, а абсолютная дата для сказанного четыре часа назад
         заставляет считать в уме. */
      today: 'Сегодня',
      yesterday: 'Вчера',
      /* Док на карточке записи (FR-46, UX-DR-13, 0052). Отдельная группа:
         здесь названо само ОКНО — его шапка и три кнопки, — тогда как всё
         выше описывает переписку внутри, а она та же самая, что и на
         странице чата. */
      dock: {
        case: 'Чат по кейсу',
        quote: 'Чат по работе',
        subject: '{{number}} · {{client}}',
        internal: 'Только свои',
        teamOnly: 'Видно только вашей команде',
        minimise: 'Свернуть чат',
        expand: 'Открыть страницу чата',
        /* Свёрнутое состояние: плавающая кнопка с подписью при наведении.
           Говорит «внутренний чат», а не называет запись, — запись и так на
           экране позади. */
        open: 'Внутренний чат',
        /* Заменяет обычную подпись, а не дополняет её: счёт должен звучать
           как часть того, зачем кнопка. */
        openWithUnread_one: 'Внутренний чат, {{count}} новое сообщение',
        openWithUnread_few: 'Внутренний чат, {{count}} новых сообщения',
        openWithUnread_many: 'Внутренний чат, {{count}} новых сообщений',
      },
    },
  },

  /* Страница чата (0029): переписка по записям, в двух вкладках.
     
     Отдельное пространство имён, а не ещё ключи внутри `cases`: страница
     охватывает и работы, и кейсы, так что вложить её в одно из двух значило бы
     неверно назвать половину того, что она показывает. Строки самой панели
     остаются в `cases.chat.*` — это те же слова, и переносить их значит
     тронуть каждое место вызова в ChatPanel без выгоды для читателя.
     
     Словарь взят из существующего каталога, а не переведён заново: в
     интерфейсе «Чат», в прозе «переписка». */
  chat: {
    title: 'Чат',
    subtitle: 'Переписка по работам, которые вы ведёте.',
    tabs: {
      cases: 'Кейсы',
      quotes: 'Работы',
    },
    emptyCases: 'Переписки по кейсам пока нет.',
    emptyQuotes: 'Переписки по работам пока нет.',
    pickOne: 'Выберите беседу, чтобы прочитать её.',
    loading: 'Загружаем беседы',
    failed: 'Не удалось загрузить беседы.',
    unread_one: '{{count}} непрочитанное',
    unread_few: '{{count}} непрочитанных',
    unread_many: '{{count}} непрочитанных',
    unreadCapped: '99+',
    archived: 'Архив — только чтение',
    archivedWhy: 'Эта работа стала кейсом. Переписка продолжается там.',
    edited: 'изменено',
    deletedBody: 'Сообщение удалено',
    edit: 'Изменить',
    editing: 'Правка',
    saveEdit: 'Сохранить',
    cancelEdit: 'Отмена',
    delete: 'Удалить',
    confirmDelete: 'Удалить сообщение? Оно останется в переписке пометкой о том, что что-то убрали.',
    editFailed: 'Не удалось изменить сообщение.',
    deleteFailed: 'Не удалось удалить сообщение.',
    noAttachmentsOnQuotes: 'Файлы прикрепляются к кейсам, а не к работам.',

    /* Собственный фильтр списка (0052). По уже загруженным строкам: сервер
       отдаёт все доступные читателю беседы одним ответом, так что сузить их —
       это проверка на подстроку, а не запрос. Подсказка своя на каждой
       вкладке, потому что и список свой: предлагать искать кейсы на вкладке
       работ значило бы описывать строки, которых там нет. */
    search: {
      cases: 'Поиск по кейсам…',
      quotes: 'Поиск по работам…',
      /* Доступное имя поля. Подсказка внутри им не является — она исчезает
         ровно тогда, когда человек начал печатать. */
      label: 'Поиск бесед',
      /* Фильтр ничего не нашёл. Это не то же, что `emptyCases` выше: беседы у
         читателя есть, просто ни одна не подходит под текст. */
      none: 'Под запрос ничего не подошло.',
    },
    /* Последняя строка беседы в её строке списка. Автор и текст одной
       строкой, а не двумя кусками, — чтобы переводчик мог двигать двоеточие. */
    preview: '{{author}}: {{body}}',

    /* Шапка переписки. Называет запись: список рядом можно отмотать от
       выбранной строки, и переписка без заголовка превращается в
       необъяснённый столбец реплик. */
    threadSubtitle: {
      case: '{{client}} · Переписка по кейсу',
      quote: '{{client}} · Переписка по работе',
    },
    /* Та же строка для записи, чьего клиента списки ещё не подтянули: лучше
       короткая правда, чем «undefined · …». */
    conversationKind: {
      case: 'Переписка по кейсу',
      quote: 'Переписка по работе',
    },
    /* Внутренняя всегда, и сказано об этом дважды намеренно: плашка рядом с
       заголовком — для тех, кто знает, и полоса под ней — для тех, кто нет.
       Внешних участников на этой поверхности нет структурно (FR-46). */
    internal: 'Только свои',
    internalBanner: 'Внутренняя переписка — видна только вашей команде',
    /* Из переписки к самой записи. Ссылка, а не действие, — открывается в
       новой вкладке обычным способом. */
    viewCase: 'Открыть кейс',
    viewQuote: 'Открыть работу',
  },

  quotes: {
    title: 'Котировки',
    subtitle: 'Что мы предложили и сколько это принесёт, если сложится.',
    loading: 'Загрузка котировки',
    backToList: 'К котировкам',
    /* Счётчик в подвале таблицы. Считает то, что видно ПОСЛЕ фильтра: подвал
       стоит под самим списком и должен отвечать за него, а не за набор,
       который экран решил не показывать. */
    count_one: '{{count}} котировка',
    count_few: '{{count}} котировки',
    count_many: '{{count}} котировок',

    /* Пять вариантов оплаты из листа Reference list исходной книги. ХРАНИТСЯ и
       печатается английская строка — это то, что читает клиент в оферте; здесь
       только подписи для нашего же коллеги в выпадающем списке. */
    paymentOptions: {
      'in-advance-payment': 'Предоплата',
      '50-50-payment-upon-pickup-delivery': '50/50 — при загрузке и при выдаче',
      'full-payment-upon-delivery-before-unloading':
        'Полная оплата при выдаче, до разгрузки',
      credit: 'В кредит',
      other: 'Другое — условия ниже',
    },

    /* Выиграно/проиграно за период (FR-15). ЧЬИ это цифры — часть самой
       цифры: у продавца они считаются по его собственным котировкам, и
       прочитать личный показатель как показатель компании — ровно та ошибка,
       от которой защищает AC 6. Поэтому оговорка стоит в самом блоке, а не в
       подсказке. «Истекло» считается отдельно и никогда не сливается с
       «Проиграно»: котировка без ответа — это не отказ клиента. */
    statistics: {
      title: 'Выиграно / проиграно',
      won: 'Выиграно',
      wonHint: 'Переведено в работу',
      lost: 'Проиграно',
      lostHint: 'Отклонено',
      expired: 'Истекло',
      expiredHint: 'Считается отдельно, не как проигрыш',
      conversion: 'Конверсия',
      /* Называет знаменатель, а не описывает его: «выиграно из решённых»
         оставляет читателя гадать, входят ли туда истёкшие. Цифры отвечают
         прямо — и это те же три показателя, что стоят рядом. */
      conversionHint: '{{won}} из {{decided}} решённых',
      wonValue: 'Сумма выигранных',
      scope: {
        own: 'Только ваши котировки',
        assigned: 'Ваши котировки и те, где вы в команде',
        all: 'Все котировки, всех владельцев',
      },
      range: {
        label: 'Период',
        d30: 'Последние 30 дней',
        d90: 'Последние 90 дней',
        year: 'С начала года',
        all: 'За всё время',
      },
      /* Дни, которые сервер действительно посчитал: переключатель называет
         запрошенный период, эта строка — тот, который вернулся. Только даты:
         строка стоит в шапке панели рядом с заголовком, и приставка «учтён
         период» повторяла бы очевидное там, где дорог каждый символ. */
      period: '{{from}} — {{to}}',
      error: 'Не удалось получить показатели побед и проигрышей.',
    },

    /* Переключатель разделов карточки КП. Названия те же, что стояли в
       заголовках карточек: читатель уже выучил «Статьи затрат» как заголовок,
       и переучивать его на «Цену» ради вкладки незачем. */
    sectionsLabel: 'Разделы котировки',
    tabs: {
      cargo: 'Груз',
      pricing: 'Статьи затрат',
      routes: 'Маршруты',
      participants: 'Участники',
      terms: 'Условия',
      revisions: 'Версии',
      history: 'История',
    },

    actions: {
      newQuote: 'Новая котировка',
      editQuote: 'Изменить котировку',
      /* Возврат в пошаговый конструктор из сохранённой котировки. Подпись
         говорит, что работа ПРОДОЛЖАЕТСЯ, а не что открывается ещё один экран:
         тот, кому это нужно, уже был там — он там же котировку и создал, ушёл и
         вернулся за наценкой, которая живёт только там. Соседнее «Изменить
         котировку» — это шторка с шапкой, другое и меньшее действие; две
         подписи должны различаться. */
      create: 'Создать котировку',
      approve: 'Клиент согласовал',
      converting: 'Создаём работу…',
      openCase: 'Открыть работу {{number}}',
      newRevision: 'Новая версия',
      reopening: 'Открываем…',
      sendOffer: 'Отправить КП',
      preview: 'Предпросмотр',
      fixCargo: 'Заполнить груз',
    },

    /* Участники по этой котировке (FR-24) — на карточке самой котировки, а не
       при создании: котировка не называет свои внешние стороны на входе, форма
       создания о них не спрашивает, и эти слова принадлежат уже существующей
       записи.
       Отдельный блок, а не переиспользование `cases.participants`: там речь о
       работе, которая уже идёт, здесь — о сделке, которая ещё не выиграна, и
       отсюда участникам ничего не отправляется. Общие строки сделали бы
       обещание «внешние стороны получают уведомления только по email» тем,
       которое этот экран не выполняет. */
    participants: {
      add: 'Добавить участника',
      role: 'Роль',
      contactHint: 'Email или телефон. Необязательно.',
      notifyByEmail: 'Уведомлять по email',
      /* Прямо сказано, что галочка пока только фиксирует согласие: иначе её
         ставят в расчёте на то, что КП уйдёт и грузополучателю — а КП уходит
         клиенту. */
      notifyHint:
        'Фиксирует согласие получать письма. Переходит в работу, где используется для статусов. Само КП уходит клиенту.',
      contactNeededForEmail: 'Укажите email, чтобы уведомлять этого участника.',

      /* Дальше — то, что есть только у уже СОХРАНЁННОЙ записи: изменение,
         удаление и состояние существующей строки. Ключи выше раньше делились с
         карточкой на шаге 1 мастера, которой больше нет; порядок сохранён —
         те же семь сторон описываются теми же словами, теперь и в работе. */
      edit: 'Изменить участника',
      count_one: '{{count}} участник',
      count_few: '{{count}} участника',
      count_many: '{{count}} участников',
      notifiedByEmail: 'Уведомляется по email',
      noContact: 'Контакт не указан',
      emptyTitle: 'Участников пока нет',
      emptyDescription:
        'Здесь появятся грузоотправители, грузополучатели, агенты и водители по этой перевозке.',
      delete: 'Убрать из котировки',
      deleteConfirm: 'Убрать эту сторону из котировки?',
      deleteConfirmAction: 'Убрать',
    },

    /* ВНУТРЕННЯЯ команда по котировке (миграция 0028) — коллеги, а не стороны.
       Отдельный блок, намеренно стоящий рядом с `participants` выше: именно на
       этой паре и возникает путаница. Там — внешние стороны перевозки
       (грузоотправитель, водитель), здесь — сотрудники ASA с учётными
       записями. Тексты должны сами нести это различие: спрашивают о них теперь
       на разных экранах — команду при создании, стороны на уже созданной
       котировке, — а «добавить» звучит одинаково и там, и там.

       Не переиспользует `cases.team` по той же причине, по которой разделены
       блоки участников: работа уже идёт, а котировка ещё не выиграна, и «ему
       это видно» значит на двух экранах разное. */
    team: {
      title: 'Команда',
      /* Прямо сказано, что даёт добавление: на котировке это не оформление —
         членство в команде и есть то, что открывает доступ. */
      hint: 'Коллеги, которые ведут эту котировку вместе с вами. Добавленный увидит её. Необязательно.',
      empty: 'Пока только вы. Добавьте коллегу, если сделку ведёт кто-то ещё.',
      add: 'Добавить коллегу',
      colleague: 'Коллега',
      /* По имени, а не по позиции: коллегу выбирают из списка, а не вводят
         вручную, поэтому имя к моменту чтения подписи уже есть. */
      remove: 'Убрать {{name}} из команды',
      /* Ответственный попадает в команду при создании котировки и остаётся
         единственным, кого нельзя убрать. */
      owner: 'Ответственный',
      you: 'Вы',
      /* Отличает «больше некого добавить» от «список не загрузился» — именно
         так читается пустой выпадающий список, если ничего не сказать. */
      noneAvailable: 'Больше некого добавить.',
      /* Два отказа сервера — словами, по которым понятно, что делать. */
      lastMember: 'Назначьте другого коллегу, прежде чем убрать последнего.',
      notAssignable: 'Эту учётную запись нельзя назначить.',
    },

    /* Создание котировки (UJ-1, FR-4, FR-5).

       Всё, что осталось от трёхшагового мастера. Шаги убраны: у каждого был
       двойник на странице котировки. Здесь спрашивается только то, что несёт
       запрос на создание, остальное заполняется уже в самой котировке. */
    create: {
      title: 'Новая котировка',
      subtitle: 'Для кого предложение. Всё остальное заполняется в самой котировке.',
      identity: 'Клиент и груз',
      identityHint: 'Для кого предложение и что едет.',
    },
    /* Карточки-справки рядом с обоими экранами котировки — только чтение. Ни
       один факт не живёт здесь единственным экземпляром: всё это есть и в
       основной колонке, поэтому на узком экране рейл можно просто убрать. */
    aside: {
      quote: 'Котировка',
      number: 'Номер',
      /* Номер выдаёт сервер (FR-5). Сказано словами, чтобы никто не искал поле,
         которого нет. */
      numberPending: 'будет присвоен при создании',
      status: 'Статус',
      owner: 'Владелец',
      readiness: 'Готовность к отправке',
      readyIdentity: 'Клиент и контакт указаны',
      readyRouting: 'Маршрут и груз заполнены',
      readyPricing: 'Цена посчитана',
      offerCarries: 'В КП попадёт',
      /* Тёмная карточка денег. Три цифры на ней подписаны словарями расчёта и
         наценки — `quotes.costLines.customerTotal`, `quotes.pricing.cost`,
         `quotes.pricing.marginOverRevenue`, — потому что те же величины стоят
         под теми же подписями на вкладке затрат. Второй словарь для одной
         величины — это две величины для того, кто читает бегло. */
      money: 'Деньги по котировке',
      /* Показывается, только пока расчёт пуст: три нуля без объяснения похожи
         на карточку, которая не загрузилась. */
      moneyNote:
        'Пока нет ни одной статьи затрат — цифры появятся здесь, как только расчёт заполнится.',
    },

    form: {
      origin: 'Откуда',
      destination: 'Куда',
      mode: 'Вид перевозки',
      incoterm: 'Инкотермс',
      /* Наша фраза о правиле, а не название самого правила: коды ИКЦ в обоих
         каталогах остаются английскими. */
      incotermSeaOnly: 'только море и внутренние водные пути',
      quoteDate: 'Дата котировки',
      paymentTerms: 'Условия оплаты',
      transitDays: 'Срок доставки по запросу клиента, дней',
      transitDaysMin: 'Минимум дней',
      transitDaysMax: 'Максимум дней',
      transitInverted: 'Минимальный срок не может быть больше максимального.',
      /* Девять полей шапки (FR-4 / FR-11 / FR-59), которые до 17.08.2026 нельзя
         было заполнить ни на одном экране: колонки в `quotes` были, DTO их
         отдавал, а формы не спрашивали. */
      clientContact: 'Контакт у клиента',
      /* Называет человека, которому уйдёт КП, если контакт не выбран: одного
         «необязательно» мало — читатель не знает, отправится ли КП вообще. */
      clientContactDefault: 'По умолчанию — {{name}}',
      cargoOwner: 'Владелец груза',
      /* Подсказка внутри поля вместо слова «Необязательно» после подписи. Она
         отвечает на вопрос, на который то слово не отвечало, — как выглядит
         хороший ответ, — и не добавляет полю ни строчки высоты. */
      cargoOwnerPlaceholder: 'Если это не клиент',
      cargoDescription: 'Описание груза',
      cargoDescriptionPlaceholder: 'Мрамор в слэбах, 15 паллет',
      pickupLocation: 'Место забора груза',
      dealTermsLegend: 'Условия сделки',
      demurrageTerms: 'Демередж после свободного времени',
      demurrageTermsHint: 'Как договорились, словами — например, 80 USD/сутки за контейнер.',
      deliveryTerms: 'Условия доставки',
      /* Говорит, чем это НЕ является: выше в той же форме стоит Инкотерм, и их
         легко перепутать. */
      deliveryTermsHint: 'Как доставка происходит на практике — это не Инкотерм.',
      carrierDisplayLegend: 'Строка перевозчика в КП',
      carrierDisplay: 'Перевозчик или поставщик, как печатается',
      /* Вторая часть критерия AC 2 истории 2.1 — там, где иначе это поле начнут
         использовать как поле вендора. */
      carrierDisplayHint:
        'Печатается в КП только для информации. Не назначает вендора ни одной строке затрат.',
      validUntilHint: 'После этой даты КП показывается как истёкшее.',
      measures: 'Вес и объём',
      grossWeightOverride: 'Вес брутто, кг',
      volumeOverride: 'Объём, м³',
      overrideHint:
        'Оставьте пустым — возьмём сумму по позициям. Значение здесь заменит её в КП.',
      chargeableWeightHint: 'На всю отправку, если перевозчик даёт одну цифру.',
      finalPackingList: 'Финальный упаковочный лист получен',
      finalPackingListHint:
        'Объёмы выше — клиентские, а не наша оценка. Останется видно в работе.',
      nextStep: 'Груз и статьи затрат добавляются в самой котировке.',
    },
    columns: {
      number: 'Котировка',
      client: 'Клиент',
      route: 'Маршрут',
      /* Описание груза из шапки котировки. Заголовок короткий — колонка узкая,
         а «Описание груза» перенесётся на две строки над ячейкой, в которой и
         так одна обрезанная строка. */
      cargo: 'Груз',
      status: 'Статус',
      validUntil: 'Действует до',
      sell: 'Продажа USD',
      margin: 'Маржа USD',
    },
    /* Квитанция за перетаскивание по доске: что и куда, одной фразой,
       с отменой рядом. */
    board: {
      moved: '{{number}} — теперь «{{status}}»',
      /* Пустая колонка — словами. Приглашение вынесено в ОТДЕЛЬНЫЙ ключ: оно
         верно только там, куда действительно можно перетащить. Над
         «Конвертирована» такая строка учила бы жесту, который эта колонка
         не примет. */
      laneEmpty: 'Нет котировок',
      laneDrop: 'Перетащите сюда для изменения статуса.',
    },
    card: {
      /* Метка отправленной версии рядом с датой: «версия A · 26 июл» — тем же
         словом, что и на экране КП; «Rev» здесь никто не говорит. */
      rev: 'версия {{label}}',
      total: 'Итого',
      margin: 'Маржа',
      validTo: 'Действует до',
      /* Тишина со стороны клиента, как её называют на планёрке. */
      noAnswer_one: 'без ответа {{count}} д',
      noAnswer_few: 'без ответа {{count}} д',
      noAnswer_many: 'без ответа {{count}} д',
      /* Смешанный груз считаем позициями, а не называем один тип упаковки. */
      positions_one: '{{count}} позиция',
      positions_few: '{{count}} позиции',
      positions_many: '{{count}} позиций',
      /* Что мешает отправить КП. На карточке — счёт, в самой котировке — что
         именно исправить (FR-3). «Не заполнена» — как говорят про позицию. */
      blocked_one: '{{count}} позиция не заполнена',
      blocked_few: '{{count}} позиции не заполнены',
      blocked_many: '{{count}} позиций не заполнено',
    },
    emptyTitle: 'Котировок пока нет',
    emptyDescription:
      'Созданные котировки появятся здесь с маржой и статусом.',

    cargo: {
      title: 'Груз',
      add: 'Добавить позицию',
      edit: 'Изменить позицию',
      position: 'Позиция',
      hsCode: 'Код ТН ВЭД',
      packages: 'Мест',
      packageType: 'Тип упаковки',
      weight: 'Вес, кг',
      volume: 'Объём, м³',
      /* В таблице груза, вместо числа: объём не измерен. */
      missing: 'Не указан',
      volumeRequired: 'Обязательно — без объёма КП не отправить',
      volumeHint: 'Сверяется с букингом до отправки КП.',
      /* Сборный груз — обычное дело: короба трёх отправителей в одной машине,
         и в КП должно быть видно, чьё что. */
      shipper: 'Отправитель',
      shipperHint: 'Только если эта позиция едет от другого отправителя.',
      /* Вводится, а не считается: делитель — перевозчика, не наш. */
      chargeableWeight: 'Оплачиваемый вес, кг',
      chargeableWeightHint: 'Только если перевозчик считает не по фактическому весу.',
      totals: 'Итого',
      overriddenMark: 'вручную',
      overriddenFrom: 'Задано вручную. По позициям выходит {{value}}.',
      partialTotal: 'По части позиций данных нет — итог неполный.',
      packingListReceived: 'Финальный упаковочный лист получен',
      dimensions: 'Габариты, см',
      lengthCm: 'Длина',
      widthCm: 'Ширина',
      heightCm: 'Высота',
      isChemical: 'Химический груз',
      chemicalNote:
        'Для химического груза нужны MSDS и сертификат на перевозку — без них КП не отправить.',
      hasMsds: 'MSDS получен',
      hasTransportCertificate: 'Сертификат на перевозку получен',
      chemical: 'Химия',
      readyTitle: 'Готово к отправке',
      readyDescription: 'Данные по грузу заполнены, КП можно отправлять.',
      emptyTitle: 'Груза пока нет',
      emptyDescription:
        'Добавьте позиции, на которые считаем КП. Пока они не заполнены, КП не отправить.',
      delete: 'Удалить позицию',
      deleteConfirm: 'Удалить позицию? Действие необратимо.',
      deleteConfirmAction: 'Удалить',
    },

    /* Чек-лист считает, что отделяет черновик от отправленного КП. */
    checklist: {
      blocked_one: '{{count}} пункт мешает отправить КП',
      blocked_few: '{{count}} пункта мешают отправить КП',
      blocked_many: '{{count}} пунктов мешают отправить КП',

      /* Пункт списка: чего не хватает и что это блокирует. Склейка здесь, а не
         в коде — тире и порядок частей у каждого языка свои. */
      line: '{{issue}} — {{blocks}}',

      /* Чего не хватает. Позицию называем по номеру — рядом таблица, где
         менеджер считает строки глазами. Форма без номера нужна для позиции,
         которую ещё не пронумеровали. */
      issue: {
        dimensionsMissing: 'Не указаны габариты в позиции {{position}}',
        dimensionsMissing_noPosition: 'Не указаны габариты',
        volumeMissing: 'Не указан объём в позиции {{position}}',
        volumeMissing_noPosition: 'Не указан объём',
        msdsMissing: 'Нет MSDS в позиции {{position}}',
        msdsMissing_noPosition: 'Нет MSDS',
        certificateMissing: 'Нет сертификата на перевозку в позиции {{position}}',
        certificateMissing_noPosition: 'Нет сертификата на перевозку',
      },

      /* Что именно это блокирует. Для химии причина строгости названа прямо —
         иначе непонятно, почему к этой позиции требований больше. */
      blocks: {
        send: 'КП не отправить',
        sendChemical: 'КП не отправить — груз химический',
      },
    },

    routes: {
      title: 'Варианты маршрута',
      transitDays_one: '{{count}} день',
      transitDays_few: '{{count}} дня',
      transitDays_many: '{{count}} дней',
      /* Короткие формы для строк A/B/C, где полная фраза не помещается. */
      daysShort: 'дн',
      transitUpTo: 'до {{count}} дн',
      transitFrom: 'от {{count}} дн',
      emptyTitle: 'Вариантов маршрута пока нет',
      emptyDescription: 'Просчитанные маршруты появятся здесь — из них клиент и выбирает.',
      add: 'Добавить вариант',
      /* Пунктирная плитка в пустой ячейке сетки. Называет, ЗАЧЕМ нужен ещё один
         маршрут, а не само действие: действие уже названо кнопкой в шапке
         карточки, а два элемента с одним именем — два элемента, которые
         скринридер не различит. */
      addNext: 'Вариант {{letter}} — клиенту на выбор',
      edit: 'Изменить вариант',
      origin: 'Откуда',
      /* FR-19: отдельное поле, чтобы при конвертации в работу маршрут не
         разбирали из строки заново. */
      borderCrossing: 'Погранпереход',
      destination: 'Куда',
      mode: 'Вид перевозки',
      indicativeTotal: 'Ориентировочная сумма, USD',
      indicativeHint:
        'Показывается на вариантах B и C. Выбранный вариант считается по статьям затрат.',
      selected: 'Выбран',
      select: 'Выбрать',
      /* Предупреждение FR-3 о перерасчёте: сервер не двигает выбор молча,
         это — его вопрос. */
      selectWarning:
        'Статьи затрат просчитаны под выбранный сейчас вариант. После переключения они будут описывать этот маршрут — проверьте их перед отправкой КП.',
      selectConfirm: 'Переключить',
      delete: 'Удалить вариант',
      deleteConfirm: 'Удалить этот вариант маршрута? В КП его больше не будет.',
      deleteConfirmAction: 'Удалить',
      /* FR-3: выбранный вариант держит просчёт. Инструкция, а не извинение —
         решается одним переключением. */
      deleteHoldsCosting:
        'Этот вариант выбран, и статьи затрат просчитаны под него. Сначала выберите другой вариант — КП не остаётся без маршрута, под который посчитана цена.',
      deleteFailed: 'Не удалось удалить вариант. Попробуйте ещё раз.',
    },

    terms: {
      title: 'Условия',
      /* Подзаголовок карточки, когда её можно править прямо здесь. Говорит, что
         правка попадёт в следующее КП, — иначе неясно, меняет ли она уже
         отправленное. */
      editHint: 'Правки попадут в следующее КП. Уже отправленные версии не меняются.',
      days_one: '{{count}} день',
      days_few: '{{count}} дня',
      days_many: '{{count}} дней',
      days_other: '{{count}} дней',
    },
    /* Третий блок референсного КП — IMPORTANT NOTES.

       «Примечание», а не «оговорка»: оговорка — юридическое слово про
       ограничение ответственности, а сюда на деле пишут «свободное время обычно
       двое суток в пункте назначения». На бумаге этот блок называется notes.
       В базе вид записи по-прежнему `disclaimer` — так его называют шаблоны и
       контракт (0032); изменился только экран. */
    notes: {
      title: 'Примечания',
      hint: 'Печатается в КП после цены.',
      frozen: 'Напечатано в отправленном КП. Отправленное КП показывает то, что получил клиент.',
      empty: 'Примечаний пока нет.',
      /* Подпись внутри поля — отдельной надписи над ним нет: заголовок карточки
         уже сказал, что это. */
      addPlaceholder: 'Добавить примечание…',
      add: 'Добавить',
      edit: 'Изменить это примечание',
      remove: 'Удалить это примечание',
    },
    /* Что цена покрывает и чего не покрывает — печатается в КП (Story 5.2,
       FR-60). Это текст, который получит клиент, поэтому формулировки здесь —
       про бумагу, а не про базу: «печатается», а не «сохраняется». Заголовки
       совпадают с шаблонами в настройках, откуда этот текст обычно и копируют.

       Оговорки, которые были здесь третьим списком, теперь `quotes.notes` —
       на своей карточке. */
    blocks: {
      title: 'Включено и не включено',
      hint: 'Печатается в КП после цены: что входит в стоимость и что в неё не входит.',
      frozen: 'Напечатано в отправленном КП. Отправленное КП показывает то, что получил клиент.',
      /* «Включено» идёт первым — в таком порядке это печатается в КП. */
      included: 'Включено',
      includedEmpty: 'Пока ничего не отмечено как включённое.',
      addIncludedPlaceholder: 'Добавить включённое…',
      addIncluded: 'Добавить включённое',
      editIncluded: 'Изменить этот пункт',
      removeIncluded: 'Удалить этот пункт',
      notIncluded: 'Не включено',
      notIncludedEmpty: 'Исключений пока нет.',
      addNotIncludedPlaceholder: 'Добавить исключение…',
      addNotIncluded: 'Добавить исключение',
      editNotIncluded: 'Изменить это исключение',
      removeNotIncluded: 'Удалить это исключение',
      delete: 'Удалить',
      capped:
        'В этом КП уже столько записей такого вида, сколько помещается в предложение. Удалите одну, чтобы освободить место.',
      loadFailed: 'Не удалось загрузить. Обновите страницу и попробуйте ещё раз.',
    },
    /* История действий (вкладка «История»): записи глобального журнала по
       этой котировке. Сервер хранит машинный ключ ("quote.cargo.create"),
       перевод живёт здесь — и в словах ASA: работа, КП, статьи затрат
       (EXPERIENCE.md, не калька из брифа). Прошедшее время везде: это факты о
       том, что произошло, а не кнопки. */
    history: {
      title: 'История',
      emptyTitle: 'Пока пусто',
      emptyDescription: 'Каждое действие по этой котировке записывается здесь.',
      actions: {
        created: 'Котировка создана',
        updated: 'Данные котировки изменены',
        cargoAdded: 'Добавлена позиция груза',
        cargoUpdated: 'Позиция груза изменена',
        cargoRemoved: 'Позиция груза удалена',
        costLineAdded: 'Добавлена статья затрат',
        costLineUpdated: 'Статья затрат изменена',
        costLineRemoved: 'Статья затрат удалена',
        routeOptionAdded: 'Добавлен вариант маршрута',
        routeOptionUpdated: 'Вариант маршрута изменён',
        routeOptionSelected: 'Выбран вариант маршрута',
        routeOptionRemoved: 'Вариант маршрута удалён',
        blockAdded: 'Добавлен блок текста КП',
        blockUpdated: 'Изменён блок текста КП',
        blockRemoved: 'Удалён блок текста КП',
        sent: 'КП отправлено клиенту',
        reopened: 'Открыта новая версия',
        answerApproved: 'Клиент согласовал',
        answerRejected: 'Клиент отклонил',
        answerRecorded: 'Записан ответ клиента',
        converted: 'По котировке создана работа',
      },
    },
    revisions: {
      title: 'Версии',
      emptyTitle: 'Версий пока нет',
      emptyDescription: 'Каждое отправленное КП сохраняется здесь в том виде, в каком ушло.',
      /* Why the button is off. The status is the reason, so the sentence
         names it rather than saying "нельзя" — a converted работа has its own
         запись, and a черновик is already editable. */
      notReopenable: 'Новую версию можно открыть только по отправленному или отклонённому КП.',
      /* Почему шапку нельзя править. Говорит, что делать дальше: отправленное КП
         меняется новой версией, а не на месте (FR-7). */
      notEditable:
        'Отправленное КП правится через новую версию, а не на месте.',
    },

    /* Sending the offer (FR-71). The sheet exists to show the address before
       письмо уходит навсегда — не чтобы спросить лишнего. */
      /* Предпросмотр КП (UX-DR-15) перед необратимой отправкой.

         Подписи самого документа сюда не входят: они приходят с сервера на языке
         клиента, потому что именно на нём печатается PDF. Здесь — только рамка
         листа, на языке пользователя. */
      preview: {
        title: 'Предпросмотр КП',
        loading: 'Собираем КП',
        close: 'Закрыть',
        /* На месте номера выданного документа. У предпросмотра номера нет —
           номер присваивается при отправке (AD-12), и «ЧЕРНОВИК» — то же слово,
           которое печатает генератор PDF. */
        draft: 'ЧЕРНОВИК',
        revision: 'ревизия {{label}}',
        signature: 'Подпись и печать',
        /* Язык САМОГО документа, а не интерфейса — поэтому подпись именно
           такая, а не просто «Язык». КП пишется для клиента, и менеджеру,
           работающему по-русски, всё равно нужно прочитать английскую
           страницу перед отправкой. */
        language: 'Язык документа',
        /* Говорит, что именно переключается: обвязка листа за переключателем
           не идёт — таблицы и подписи полей ниже принадлежат клиенту, кнопки
           вокруг них остаются на языке читающего. */
        languageHint: 'На этом языке документ прочитает клиент. Отправка возьмёт тот же язык.',
        blockedTitle: 'Отправить пока нельзя',
        blockedNoCargo: 'В КП нет груза. Добавьте позиции на шаге 2 — КП без позиций ничего не оценивает.',
        blockedChecklist: 'Чек-лист по грузу ещё открыт. На шаге 2 указано, чего не хватает.',
        blockedNotDraft: 'Это КП уже выпущено. Откройте новую ревизию, чтобы отправить снова.',
        blockedNoRecipient: 'У клиента нет контакта с почтой. Добавьте его в справочнике.',
        blockedGeneric: 'Сервер пока не выпускает это КП.',
      },
    offerSend: {
      title: 'Отправить КП',
      document: 'Документ',
      nextRevision: 'версия {{label}}',
      validUntil: 'Действительно до {{date}}',
      recipient: 'Получатель',
      noRecipient: 'У клиента нет контакта с почтой. Добавьте его в справочнике — без адреса КП отправить некуда.',
      /* Отказа по FR-64 здесь больше НЕТ: он в `errors.branding`, потому что КП
         больше не единственный документ, который он может остановить. */
      explainer: 'КП сохранится как неизменяемая версия, PDF попадёт в файлы, а клиент получит письмо со ссылкой на документ.',
    },
    costLines: {
      title: 'Статьи затрат',
      add: 'Добавить статью',
      edit: 'Изменить статью',
      description: 'Статья',
      vendor: 'Поставщик',
      /* Подсказка под полем поставщика. Говорит ровно то, что решает миграция
         0019: справочник здесь подсказывает, а не ограничивает. */
      vendorHint: 'Выберите из справочника или впишите своё',
      vendorPlaceholder: 'Начните вводить название',
      vendorNoMatches: 'В справочнике нет такого контрагента',
      vendorFreeTextHint: 'Введённое название сохранится как есть',
      qty: 'Кол-во',
      quantity: 'Количество',
      /* ПЕРВАЯ из двух денежных колонок, и это СЕБЕСТОИМОСТЬ единицы —
         то, что оператор вводит руками, как в блоке INTERNAL COST AND SELLING
         исходной рабочей книги. Цена клиенту получается из неё наценкой
         (quotes.pricing ниже), то есть считается, а не вводится.

         «Цена за единицу», а не «Себестоимость»: так называется колонка в
         книге и так это называет оператор. Панель, которая читает
         себестоимость обратно, подписывает свой итог «Себестоимость» — другая
         величина под другим именем, и это намеренно. */
      unitPrice: 'Цена за единицу',
      /* ВТОРАЯ денежная колонка: цена за единицу, умноженная на количество, —
         та самая «сумма», которая в книге стоит рядом с «ценой за единицу».

         Обе попытки обойтись одной колонкой сломаны, потому здесь их две. Одна
         колонка с заголовком «Цена за единицу», в которой напечатана сумма, —
         это заголовок, который врёт про свою же ячейку; одна колонка с
         заголовком «Сумма», в которой стоит поле ввода, — это цифра, которую
         вводят руками, под словом для цифры, которую не вводят. Здесь колонка
         только читается — и в обычной строке, и в открытой: это арифметика по
         двум соседним ячейкам, а не ввод. */
      amount: 'Сумма',
      /* Память ставки СООБЩАЕТ, а не заполняет молча (FR-17). Принятая
         подсказка кладёт цифру в поле цены за единицу — её видно и можно
         перебить до сохранения. */
      rateMemory: 'В прошлый раз по этому маршруту: {{amount}} · {{case}} · {{month}}',
      useRate: 'Взять',
      /* Подвал в порядке рабочей книги: во что котировка обходится, что к этому
         добавлено, сколько платит клиент. Эти три слова — весь словарь данной
         поверхности, и они же стоят в списке на телефоне, в шапке мастера и в
         панели наценки: величина, названная в одном месте «Цена», а в другом
         «Себестоимость, итого», для читателя — две разные величины.

         «Себестоимость, итого», а не прежнее «Итого»: строка называет ОДНУ
         сторону котировки, а строка «Итого» над строкой «Маржа» читается как
         сумма всего, а не как первая половина этой суммы. «Маржа» и «Итого
         клиенту» — слова со счёта, а не переводы английских подписей. */
      costTotal: 'Себестоимость, итого',
      margin: 'Маржа',
      customerTotal: 'Итого клиенту',
      delete: 'Удалить статью',
      /* States what goes, not "are you sure": a line taking its share of the
         margin with it is the consequence worth naming. */
      deleteConfirm: 'Удалить статью вместе с её суммой в марже?',
      deleteConfirmAction: 'Удалить',
    },

    /* Расчёт цены (FR-1, FR-49, FR-69): попросить коллегу назвать цифру — и
       посчитать себестоимость, когда она есть.

       Два действия под одним заголовком, потому что это две половины одного
       вопроса: кто решает эту цену и во что она выливается. Передача — это
       ПРОСЬБА, а не просто доступ: «вас добавили к котировке» — это сведения, а
       «от вас ждут цену» — это работа, и сервер публикует их разными темами. */
    pricing: {
      desk: 'Цена',
      deskHint:
        'Кто решает эту цену. Коллега, добавленный здесь, получает просьбу назвать цену и доступ к котировке.',
      colleague: 'Коллега',
      /* Два завершённых состояния списка, и они намеренно разными словами:
         «все уже здесь» — это НОРМА для маленькой установки (две учётные
         записи, обе в котировке), а общее «Ничего не найдено» превращало
         обычный случай в поломанный контрол. Пустой список сотрудников — другой
         факт: просить пока некого, сначала нужно завести учётную запись. */
      allColleaguesAsked: 'Все, кого можно попросить, уже участвуют в этой котировке.',
      noColleagues: 'Просить больше некого — других учётных записей пока нет.',
      askForPrice: 'Запросить цену',
      noteLabel: 'Сообщение коллеге',
      /* Одна фраза, а не бриф: подробности живут в самой котировке, а записку
         длиной в расчёт в колокольчике никто не читает. */
      noteHint: 'Одна строка — что нужно и к какому сроку. Необязательно.',
      /* Показывается после успешной записи: смысл кнопки в том, что человеку
         сообщили. Без этого нажатие выглядит как добавление строки в список. */
      notified: 'Запрошено — уведомление отправлено.',
      /* ПРЕДУПРЕЖДЕНИЕ, а не запрет. `sendable` и чек-лист этим не трогаются:
         котировка, посчитанная тем же менеджером, который её открыл, — обычная
         котировка, и запрет на отправку остановил бы ежедневную работу. */
      notReviewed: 'Цену никто из отдела цен не проверял',
      notReviewedBody:
        'Отправить предложение всё равно можно. Если цифру стоит перепроверить — попросите коллегу выше.',

      /* Панель наценки. Два процента, две подписи — см. MarginPanel.

         `customerTotal` здесь НЕТ намеренно. Панель печатает `totals.sell` —
         ту же величину, что и подвал таблицы себестоимости; с тех пор как
         панель встала на вкладку расчёта, обе стоят на одном экране в
         нескольких сантиметрах друг от друга. Подпись берётся из
         `quotes.costLines.customerTotal` — словаря самого расчёта, общего со
         списком на телефоне и шапкой мастера. Вторая запись здесь держала ту
         же строку и была в одной правке от того, чтобы одна величина выглядела
         как две. */
      title: 'Цена от себестоимости',
      cost: 'Себестоимость',
      /* Подпись называет знаменатель: рядом стоит процент от ДРУГОЙ величины, и
         пара читается верно, только когда каждая цифра говорит, от чего она. */
      marginOverRevenue: 'Маржа от выручки',
      markup: 'Наценка на себестоимость, %',
      markupHint:
        'Цифра из рабочей книги: продажа каждой строки становится её закупкой плюс столько.',
      applyMarkup: 'Применить',
      applyMarkupConfirm:
        'Пересчитает все строки, включая проставленные вручную. Наценка — величина на всю котировку.',
      /* Острый угол расчёта по одной колонке — сказан рядом с кнопкой, а не в
         комментарии. Наценка нигде не хранится: в книге она лежит в ячейке, в
         этой схеме под неё нет колонки, — а сохранение строки пишет введённую
         цену в ОБЕ стороны. Поэтому строка, изменённая после применения
         наценки, возвращается к себестоимости, пока соседние сохраняют свои
         цены. Потом об этом никто не предупредит, поэтому предупреждаем до. */
      markupNotStored:
        'Наценка нигде не хранится. Изменённая после неё строка вернётся к себестоимости, а остальные сохранят свои цены, — поэтому применяйте наценку заново, когда закончите правки.',
      emptyCosting: 'Сначала добавьте статью затрат.',
      /* ДРУГОЙ отказ, и намеренно другими словами. Наценка считается от
         себестоимости — продажа становится закупкой плюс столько, — поэтому в
         калькуляции, где себестоимость всех строк нулевая, ей не от чего
         считать, а нажатие обнулило бы все цены. Фраза говорит, что делает
         кнопка и почему здесь не может; в поле она не отправляет, потому что на
         этой форме такого поля нет. */
      noCostSide:
        'Наценка считается от себестоимости строки, а здесь себестоимость всех строк нулевая — считать не от чего.',
      preset: 'Типовой набор',
      presetHint:
        'Постоянные статьи затрат, вставляются без сумм. Ведутся в настройках.',
      noPreset: 'В настройках пока нет постоянных статей затрат.',
    },
  },

  finance: {
    title: 'Деньги',
    subtitle: 'Кто кому должен.',
    sectionsLabel: 'Разделы финансов',

    tabs: {
      balances: 'Балансы',
      aging: 'По срокам',
      payments: 'Платежи',
      approvals: 'Согласования',
      documents: 'Документы',
    },

    balances: {
      counterparty: 'Контрагент',
      type: 'Тип',
      receivable: 'Дебиторка',
      payable: 'Кредиторка',
      net: 'Сальдо',
      oldest: 'Самый старый',
      /* Возраст самой старой непогашенной позиции в днях. Всегда есть и со
         ЗНАКОМ: реальное чтение присылает число в каждой строке, а контрагент,
         которому нечего гасить, в список не попадает. Отрицательный возраст —
         срок ещё не наступил — рендерится словами корзины старения, а не вторым
         вариантом фразы здесь. */
      oldestDays_one: '{{count}} день',
      oldestDays_few: '{{count}} дня',
      oldestDays_many: '{{count}} дней',
      tableLabel: 'Балансы — {{entity}}',
      emptyTitle: 'За этот период балансов нет',
      emptyDescription:
        'Контрагент появится здесь, когда по работе пройдут деньги: выставленный счёт, срок транша или проведённые затраты поставщика.',
    },

    aging: {
      title: 'Дебиторка по срокам',
      /* Пять групп, а не три. Две дополнительные и несут весь смысл экрана:
         FR-34 AC 5 запрещает читать «не погашено» как «просрочено», а AC 6 —
         сваливать позицию без даты в произвольную корзину. */
      buckets: {
        'not-yet-due': 'Срок не наступил',
        '0-30': '0–30 дней',
        '31-60': '31–60 дней',
        '60-plus': '60+ дней',
        'no-date': 'Без ожидаемой даты',
      },
      emptyTitle: 'За этот период непогашенного нет',
      emptyDescription:
        'Транши, выставленные счета и затраты поставщиков остаются здесь, пока не закрыты.',
    },

    payments: {
      title: 'Платежи',
      record: 'Внести платёж',
      edit: 'Изменить платёж',
      reference: 'Основание',
      referenceHint: 'Банковское назначение платежа — как в выписке.',
      received: 'Получен',
      amount: 'Сумма',
      currency: 'Валюта',
      direction: 'Направление',
      in: 'Входящий',
      out: 'Исходящий',
      directionLabel: 'Направление',
      allocated: 'Разнесено',
      unallocated: 'Не разнесено',
      detailTitle: 'Платёж {{reference}}',
      emptyTitle: 'За этот период платежей нет',
      emptyDescription:
        'Деньги на входе и выходе появляются здесь по мере внесения — вместе с тем, что каждый платёж закрывает.',
      case: 'Дело',
      caseHint: 'Сделка, по которой прошли деньги.',
      method: 'Способ',
      methods: {
        cash: 'Наличные',
        bank_transfer: 'Банковский перевод',
      },
      /* НАШЕ юрлицо, не контрагент: два прочтения «issuing entity» дают разный
         учёт, поэтому в названии поля сказано, чьё именно. */
      entity: 'Наше юрлицо',
      entityHint:
        'Какое из наших юрлиц получило или отправило деньги — не контрагент.',
      bookedAt: 'Учтено по {{rate}} на {{date}} · {{usd}} USD',
      allocationNote:
        'Платёж поступает неразнесённым. Разнесите его по счетам отдельным действием.',

      /* Контрагент выбирается из справочника, а не набирается руками.
         Проверкой это не является: имя остаётся свободным текстом (0035).
         Смысл — в написании: баланс по контрагенту считается ПО ИМЕНИ, поэтому
         «Зурдинов Руслан» и «Зурдинов  Руслан» — сегодня два разных баланса. */
      counterpartyHint:
        'Кто прислал деньги или кому мы их отправили — как в платёжке. Выбор из справочника нужен, чтобы одно и то же имя не разошлось по двум балансам.',
      counterpartyPlaceholder: 'Выберите контрагента',
      counterpartySearch: 'Поиск по справочнику',
      /* Клиент и участник лежат в двух разных справочниках, но в этом списке
         они рядом: деньги приходят от клиента и уходят перевозчику. Подсказка
         справа говорит, из какого справочника строка. */
      counterpartyKindClient: 'Клиент',
      /* Имя, сохранённое до того, как поле стало выбором из списка, — или
         списанное с платёжки для компании, которой в справочнике нет. Остаётся
         выбранным: ничто здесь не вправе отвергнуть уже сохранённые данные. */
      counterpartyAsEntered: 'как введено',
      counterpartyAdd: 'Добавить контрагента',

      /* Комиссия банка за перевод (миграция 0058).
         ПУСТО значит «комиссия не записана», а не «комиссия равна нулю»: ноль
         в отчёте читался бы как перевод, который ничего не стоил. */
      feeHeading: 'Комиссия за перевод',
      fee: 'Комиссия',
      feeCurrency: 'Валюта комиссии',
      feeHint:
        'Сколько банк удержал за перевод. Необязательно. Эти деньги не доходят до контрагента и ничего не закрывают — закрывает только сумма платежа выше.',
      /* На карточке платежа, отдельной строкой рядом с суммой. Складывать эти
         два числа нельзя, поэтому они и не стоят в одном итоге. */
      feeRecorded: 'Комиссия за перевод — {{amount}}',

      /* Подтверждение записи (0059, L3/L4/L8).
       *
       * Каждая фраза здесь — про ЗАПИСЬ и ни одна не про деньги.
       * Неподтверждённый платёж входит в «Оплачено», в балансы и в старение
       * наравне с подтверждённым, поэтому ничто не должно читаться как «эти
       * деньги ещё не считаются» — именно такая формулировка отправляет
       * заплатить перевозчику второй раз.
       *
       * Статуса «отклонён» нет, потому что отклонение — это УДАЛЕНИЕ:
       * отклонённая строка, оставшаяся в реестре, — цифра, которую каждая сумма
       * потом обязана не забыть исключить. */
      status: 'Статус',
      statuses: {
        pending: 'Ждёт подтверждения',
        confirmed: 'Подтверждён',
      },
      /* Метка в строке. Короткая — она стоит рядом с основанием и суммой;
         длинная форма живёт в колонке статуса и в фильтре. */
      pendingTag: 'не подтверждён',
      pendingFilter: 'Ждут подтверждения ({{count}})',
      confirm: 'Подтвердить',
      confirmedOn: 'Подтверждён {{date}}',
      reject: 'Отклонить',
      rejectTitle: 'Отклонить платёж {{reference}}',
      rejectBody:
        'Запись удалится вместе с её привязками. Долг перевозчику при этом остаётся — фактическая затрата никуда не денется. Отклонить можно только то, что ещё никто не подтвердил.',
      rejectReason: 'Причина',
      rejectReasonHint:
        'Обязательно. Строки сейчас не станет — журнал аудита останется единственным местом, где сказано почему.',
      rejectSubmit: 'Отклонить и удалить',
      amendTitle: 'Исправить платёж {{reference}}',
      amendSubmit: 'Сохранить исправление',
      amendFixedFacts:
        'Валюту ({{currency}}) и дату движения денег ({{date}}) исправить нельзя — на них держатся все букинги этого платежа. Если ошибка в них — отклоните запись с причиной и внесите заново.',
      amendAction: 'Исправить платёж {{reference}}',
      rejectAction: 'Отклонить платёж {{reference}}',
    },

    /* Разнесение платежа (история 4.5, AD-11).
     *
     * Текст не скрывает факта: разносить пока не на что. Транши появятся в
     * истории 4.3, счета и дебет-ноты — в эпике 5, и до этого идентификатор
     * приходится вводить вручную. Сказать об этом честнее, чем показать
     * выпадающий список без вариантов. */
    allocations: {
      heading: 'Разнесено на',
      allocate: 'Разнести',
      submit: 'Разнести',
      title: 'Разнести {{reference}}',
      none: 'Пока не разнесено. Неразнесённые деньги — это нормально: они разносятся, только когда человек укажет, что именно они закрывают, и никогда автоматически.',
      fullyAllocated: 'Платёж разнесён полностью — разносить больше нечего.',
      target: 'Закрывает',
      targetPlaceholder: 'Выберите транш',
      targetN: 'Что закрывает часть {{n}}',
      targetRequired: 'Укажите, что закрывает эта часть.',
      trancheLabel: 'Транш {{sequence}} из {{total}}',
      trancheHint: '{{amount}} · {{when}}',
      trancheNoDate: 'дата не согласована',
      targetsLoading: 'Читаем ожидаемые транши по этой работе…',
      targetsNone:
        'По этой работе нет ожидаемых траншей, поэтому платежу нечего закрывать. Транши создаются, когда согласована ставка по работе, — сначала внесите ставку, потом разносите.',
      targetsTrancheOnly:
        'Платёж закрывает ожидаемые транши по работе. Счета и дебет-ноты закрывать пока нельзя.',
      /* ИСХОДЯЩИЙ платёж закрывает затрату, а не транш: сервер отказывает на
         несовпадении направления (`ErrAllocationDirectionMismatch`), так что
         предлагать здесь транши означало предлагать единственное, что заведомо
         не сработает. Пустое состояние называет действие, которое это меняет, а
         не повторяет совет про ставку клиента — к оплате перевозчика он
         отношения не имеет. */
      expenseTargetsNote:
        'Фактические затраты по работе — исходящий платёж закрывает именно их, а не транши клиента.',
      expenseTargetsNone:
        'По этой работе нет фактических затрат, поэтому платежу нечего закрывать. Сначала запишите долг — «Оплатить перевозчику» на направлении делает и то и другое одним действием.',
      /* Сказано на КНОПКЕ, а не внутри окна. Открыть список, чей запрос ещё не
         ответил, значит написать «затрат нет» про работу, где их может быть
         несколько. */
      expenseTargetsLoading: 'Читаем фактические затраты по работе…',
      expenseTargetsFailed:
        'Не удалось прочитать затраты по этой работе — предлагать нечего. Повторите попытку перед разнесением.',
      targetsFailed:
        'Не удалось прочитать транши по этой работе, поэтому список может быть неполным. Повторите попытку перед разнесением.',
      amountN: 'Сумма части {{n}}',
      amountHint: 'В {{currency}} — валюте, в которой пришёл платёж.',
      add: 'Добавить часть',
      remove: 'Удалить часть {{n}}',
      requested: 'Разносится',
      splitsRequired: 'Укажите документ и сумму в каждой части.',
      exceedsBalance:
        'Сумма частей больше, чем {{balance}}, которые ещё не разнесены по этому платежу.',
      settledAt: 'по {{rate}} · {{usd}} USD',
      /* Колонки списка распределений. «Проведено» называет ячейку «по 1 ·
         2 500.00 USD» — курс, по которому взято это распределение, и во
         сколько оно вышло в USD: это проводка ЦЕЛИ, а не платежа. */
      columns: { target: 'Закрывает', amount: 'Зачтено', booked: 'Проведено', at: 'Распределено' },
      fxDifference: 'Курс {{value}}',
      fxDifferenceHint:
        'Разница между курсом закрытия и курсом, по которому был учтён документ. Записывается здесь; учтённая сумма документа не меняется.',
    },

    approvals: {
      pending: 'Ожидает',
      current: 'Текущая',
      requested: 'Запрошена',
      change: 'Изменение',
      approve: 'Согласовать',
      reject: 'Отклонить',
      confirmTitle: 'Согласовать изменение ставки по {{number}}',
      confirmBody:
        'Ставка по {{number}} изменится с {{current}} на {{requested}}. Решение обязывающее — отменить нельзя, для исправления потребуется новый запрос.',
      emptyTitle: 'Согласований нет',
      emptyDescription:
        'Изменения ставки, требующие решения, появятся здесь и останутся до ответа.',
    },

    documents: {
      title: 'Счета и дебет-ноты',
      newInvoice: 'Новый счёт',
      edit: 'Изменить документ',
      number: 'Номер',
      numberNote: 'Номер присваивается по схеме нумерации этого юрлица.',
      entity: 'Юрлицо',
      case: 'Работа',
      issued: 'Выставлен',
      status: 'Статус',
      due: 'Срок',
      total: 'Итого',
      outstanding: 'Остаток',

      /* Всё, что ниже, — это форма «создать → сформировать → выпустить» и её
       * предпросмотр, и лежит она под `finance`, а не под `cases`, намеренно:
       * раздел «Документы» в работе и страница «Финансы» создают и смотрят
       * документы ОДНИМ компонентом и одной мутацией. Работа добавляет фильтр
       * и подстановку известных ей фактов, но форму не разветвляет. Вторая
       * копия этих строк под `cases` была бы первым шагом к такому ветвлению:
       * экраны разойдутся в словах раньше, чем разойдутся в поведении.
       */

      /* Два списка, на которые разделён раздел, и состояние строки внутри них.
         «Не сформирован» именно написано, а не оставлено пустым: пустая ячейка
         в архиве читается как незагрузившаяся колонка, а не как документ,
         который ещё никто не сформировал. */
      debitNotes: 'Дебет-ноты',
      invoices: 'Инвойсы',
      rendered: 'Сформирован',
      notRendered: 'Не сформирован',

      /* Три действия, три вызова сервера и намеренно ни одного сокращения,
         которое делает два сразу: сформировать — не значит выпустить
         (UX-DR-15), а флаг «выпустить при формировании» клиент однажды
         выставит по ошибке. «Сформировать заново» — отдельная формулировка,
         потому что повторное нажатие заменяет файл, который уже лежит в
         архиве, и кнопка с прежней надписью читалась бы как первое
         формирование. */
      actions: {
        generate: 'Сформировать PDF',
        regenerate: 'Сформировать заново',
        generating: 'Формируем…',
        issue: 'Выпустить',
        issuing: 'Выпускаем…',
        /* Действия строкой в таблице. Значки — но подпись у каждого есть: она
           читается вслух и всплывает при наведении, а решение по двум опасным
           (выпуск и удаление) принимается уже в диалоге, где всё написано
           словами. Значок здесь открывает разговор, а не совершает поступок. */
        preview: 'Посмотреть документ',
        open: 'Открыть счёт',
        discard: 'Удалить черновик',
      },

      /* Удаление возможно только у черновика. Выпущенный документ клиент держит
         на руках, и номер, который никуда не ведёт, хуже документа, который
         придётся объяснять (AD-24). Про потолок сказано отдельно, потому что
         это и есть повод удалять: позиции брошенного черновика съедают остаток
         согласованной ставки у всех следующих счетов. */
      discardConfirm: {
        title: 'Удалить черновик?',
        body: 'Черновик и его позиции будут удалены безвозвратно. Пока черновик существует, его суммы занимают часть согласованной ставки и уменьшают остаток, доступный следующим счетам.',
        confirm: 'Удалить',
      },

      /* Подтверждение стоит на выпуске, потому что необратим именно он.
         Формировать можно хоть весь день — это стоит только рендера; выпуск же
         присваивает номер, фиксирует документ и создаёт дебиторскую
         задолженность, и AD-24 делает этот переход односторонним. В тексте
         названы все три последствия, а затем сказано, как исправлять:
         «отменить нельзя» само по себе читается как тупик, а не как обычный
         бумажный порядок. */
      issueConfirm: {
        title: 'Выпустить документ?',
        body: 'При выпуске документу присваивается номер по схеме нумерации этого юрлица, документ фиксируется, и по нему возникает дебиторская задолженность. Отменить это нельзя — исправление оформляется новым документом по этой же работе.',
        confirm: 'Выпустить',
      },

      /* AC 7, и блокирующее сообщение в принятом здесь виде: запрет и причина
         вместе, чтобы правило запомнилось с первого раза. Номер печатается на
         архивном файле, поэтому у несформированного документа его просто не на
         чем напечатать — и сказано именно это, а не «пока нельзя». */
      needsRender: 'Сначала нужно сформировать PDF — номер печатается на архивном файле, и без него выпустить документ не на чем.',
      /* AC 9. Это подсказка интерфейса, а не защита: сервер откажет в правке в
         любом случае. Фраза нужна, чтобы экран объяснил, почему полей нет, а
         не показывал поля, которые всё равно не примут. */
      issuedReadOnly: 'Выпущенный документ изменить нельзя. Чтобы исправить, создайте новый.',
      /* Отказ, который получает ВТОРОЕ нажатие «Выпустить». Сформулирован как
         факт о первом нажатии, а не как ошибка: человеку нужно понять, что
         документ ушёл. Проглоченный отказ или отказ в виде сбоя учат ровно
         обратному (UX-DR-24). */
      alreadyIssued: 'Этот документ уже выпущен.',
      /* Модуль `document` может отсутствовать: без настроенного объектного
         хранилища main.go не создаёт его, маршруты не подключаются, и ноутбук
         без бакета получает 404. Показывать это пустым списком нельзя — пустой
         список говорит, что по работе нет бумаг, а правда в том, что смотреть
         не в чем. */
      archiveUnavailable: 'Архив документов в этом окружении не настроен, поэтому здесь ничего нельзя сформировать и показать.',

      /* Словом «предпросмотр» названы две разные вещи, и различает их именно
         этот текст. ПРЕДПРОСМОТР ЧЕРНОВИКА клиент рисует сам из полей на
         экране — без рендера и без записи в архиве, — чтобы цикл «набрал
         строку → увидел → поправил» не стоил рендера страницы на каждое
         нажатие клавиши. ГОТОВЫЙ ДОКУМЕНТ — это сам архивный PDF, те самые
         байты, которые получит клиент. Тот, кто видел только первое, документа
         не видел; поэтому второе и существует, и поэтому ни одно из них не
         занимает слова и действия другого. */
      preview: {
        /* Окно, показывающее страницу, СОБРАННУЮ СЕРВЕРОМ, — тем же шаблоном,
           из которого печатается PDF для клиента. Стоит в одной ветке с
           надписями ниже намеренно: те описывают нарисованную вручную разметку,
           которую это заменяет, и соседство делает пару заметной тому, кто
           будет убирать остатки старого. */
        title: 'Документ',
        subtitle: 'Так документ выглядит на бумаге. У черновика вместо номера печатается слово DRAFT.',
        composeFailed: 'Не удалось собрать страницу.',

        draftBadge: 'Черновик',
        draftTitle: 'Предпросмотр черновика',
        draftHint: 'Это разметка того, что набрано, а не сам документ. Нажмите «Сформировать PDF», чтобы получить файл, который получит клиент.',
        renderedTitle: 'Готовый документ',
        /* AC 8. Показывается вместо вчерашнего PDF рядом с сегодняшними
           суммами: экран возвращается к живому черновику и говорит, на что
           именно человек сейчас смотрит, а не оставляет молча устаревший
           кадр, который выглядит не менее убедительно. */
        outOfDate: 'Черновик изменился после формирования, поэтому в архиве лежит устаревшая копия. Сформируйте заново перед выпуском.',
        /* Подписанная ссылка живёт пять минут (AD-18) — чтобы скопированная со
           страницы ссылка умерла раньше, чем её успеют переслать. Срок назван
           затем, чтобы кнопка повтора рядом читалась как обычный следующий
           шаг, а не как сбой по вине читающего. */
        failed: 'Не удалось загрузить документ. Ссылка на него живёт пять минут.',
        loading: 'Загружаем документ…',
        empty: 'Выберите документ слева, чтобы посмотреть его.',
        /* Доступное имя для <iframe>. Без него скринридер объявляет посреди
           страницы безымянный фрейм, и понять, что в нём, нечем. */
        frameTitle: 'Предпросмотр документа',
      },

      /* Сама форма создания — одна форма на оба экрана. */
      raise: {
        kind: 'Тип документа',
        counterparty: 'Кому выставляем',
        /* Это не косметика, поэтому и сказано вслух: название и адрес в
           печатной форме берутся от выбранной здесь стороны (AD-26). То есть
           этот выбор решает, что клиент прочитает в шапке документа, а не
           только то, в какой строке дебет-нота будет числиться. */
        counterpartyHint: 'Дебет-нота выставляется клиенту или одному из участников этой работы. Название и адрес в печатной форме берутся от выбранной здесь стороны (AD-26).',
        reference: 'Их номер',
        noteDate: 'Дата документа',
        currency: 'Валюта',
        /* Позиции счёта сервер копирует из принятой котировки. Сказано затем,
           чтобы никто не искал в этой форме таблицу начислений, которой здесь
           намеренно нет. */
        submit: 'Создать',
      },

      /* Развилка под кнопкой «Новый документ». Две бумаги — это не два вида
       * одной формы: счёт выставляется КЛИЕНТУ из согласованной с ним ставки
       * (AD-10), дебет-нота перевыставляет расход тому, кто его должен (AD-26).
       * Раньше вопрос не задавался вовсе: первый экран предлагал отметить
       * расходы работы, и любая галочка молча превращала документ в дебет-ноту.
       * Поэтому у каждого пункта своя фраза — из двух слов не видно, к какой
       * стороне сделки обращена бумага и откуда возьмутся её строки. */
      new: {
        invoice: 'Счёт клиенту',
        invoiceHint: 'Из согласованной ставки. Строки подставятся из котировки.',
        debitNote: 'Дебет-нота',
        debitNoteHint: 'Перевыставить расходы работы контрагенту.',
      },

      lines: {
        label: 'Позиции',
        /* Дебет-нота — это её позиции: клиенту выставляют то, чего не было в
           котировке, поэтому «за что именно» и есть весь вопрос. */
        empty: 'Позиций пока нет. Дебет-ноте нужна хотя бы одна — клиенту выставляют то, чего не было в котировке.',
        add: 'Добавить позицию',
        description: 'За что начислено',
        descriptionN: 'Позиция {{n}}: описание',
        notePlaceholder: 'Уточнение, напр. 4 контейнера (Open-top) — необязательно',
        noteN: 'Позиция {{n}}: уточнение',
        unitPrice: 'Цена',
        unitPriceN: 'Позиция {{n}}: цена',
        quantity: 'Кол-во',
        quantityN: 'Позиция {{n}}: количество',
        remove: 'Удалить позицию {{n}}',
      },
    },

    /* Рабочее место счёта — `routes/CaseInvoicePage.tsx`.
     *
     * СТРАНИЦА, а не третье окно в цепочке: счёт печатается на A4 и читается
     * поперёк — позиции, потолок ставки, по которому их судят, и проводка,
     * которая переводит их в USD, — это одна картина, а колонка в 440 пикселей
     * вмещает от силы одну её треть.
     *
     * Тексты лежат под `finance`, рядом с `documents` и `invoices`, а не под
     * `cases`, по той же причине, что и блок выше: раздел работы и страница
     * «Деньги» смотрят на одну и ту же бумагу, и второй словарь для одной из
     * них — это место, где два экрана начинают описывать одну запись
     * по-разному. */
    invoiceDesk: {
      /* Путь назад. Называет и работу, и раздел: читатель пришёл из одного из
         двух мест, и оба они — этот раздел. */
      back: 'Счета',
      title: 'Счёт клиенту',
      trancheN: 'Транш {{n}}',
      /* Счёт, который не закрывает ни один платёж по графику. Не «нет» и не
         пустое поле: доплата, перевыставление и работа вообще без графика —
         это обычные случаи, и формулировка должна читаться как осознанный
         ответ, а не как незаполненное поле. */
      offSchedule: 'Вне графика',
      /* Подпись варианта, пока список траншей ещё грузится или его чтение не
         удалось: связь на счёте есть в любом случае, и селект, откатившийся к
         «вне графика», сказал бы обратное тому, что написано в записи. */
      trancheLinked: 'Платёж по графику',
      trancheOption: 'Транш {{n}} · {{amount}}',
      numberOnIssue: 'номер присвоится при выпуске',
      issuedOn: 'Выпущен {{date}}',
      closedTranche: 'закрыл транш {{n}}',

      whatWeBill: 'Что выставляем',
      tranche: 'Платёж по договору',
      /* Сказано до нажатия, а не после. Выпуск счёта на транш убирает этот
         транш из реестра целей — счёт встаёт на его место, — и читатель, не
         знающий этого, воспримет исчезновение как пропажу данных. */
      trancheClosesNote:
        'Выпуск закроет транш {{n}}: он перестанет считаться отдельной ожидаемой суммой, чтобы дебиторка не удвоилась.',
      offScheduleNote:
        'Ни один транш не закроется: график платежей останется как был, а этот счёт встанет в дебиторку сам по себе. Потолок ставки на него всё равно распространяется.',
      /* AD-9: валюта — один из пяти фактов, зафиксированных вместе при
         создании, поэтому её показывают, а не предлагают. */
      currencyFrozen: 'Зафиксирована вместе с курсом. Другая валюта — это новый счёт.',

      lines: 'Строки счёта',
      linesFromQuote:
        'Подставлены из принятой котировки {{quote}}. Правятся прямо здесь, по одной позиции.',
      linesOwn:
        'Работу завели без котировки, поэтому подставлять было нечего — позиции набираются здесь.',
      fromQuote: 'из котировки',

      /* Транш, который закрывает счёт, и сумма его позиций должны сходиться, и
         на сервере это ничем не обеспечено. Плашка называет расхождение, но не
         закрывает его: свести их — это решение (разделить позиции, снять счёт
         с графика, перекроить транш), и ни одно из них не кнопка, которую этот
         экран вправе нажать за человека. */
      mismatchTitle: 'Строки не сходятся с траншем {{n}}',
      mismatchBody:
        'Сумма строк — {{lines}}, а транш {{n}} ожидает {{tranche}}. Выпустив счёт как есть, вы закроете этот платёж документом на другую сумму, и остаток графика останется без покрытия. Поправьте строки или переведите счёт вне графика.',

      ceiling: 'Ставка по работе',
      ceilingApproved: 'Согласовано с клиентом',
      ceilingEarlier: 'Выставлено раньше',
      ceilingThis: 'В этом счёте',
      ceilingRemaining: 'Останется на следующий счёт',
      /* У работы нет согласованной ставки — значит, потолка НЕТ, а не что он
         равен нулю. Счёт всё равно можно выставить: ему просто не с чем
         сверяться. */
      noCeilingTitle: 'Ставка не согласована',
      noCeilingBody:
        'Работу завели напрямую, без котировки, поэтому строки подставлять неоткуда и потолка у счёта нет. Счёт выставить можно — он просто не будет ни с чем сверяться.',

      booking: 'Как считается в USD',
      bookingCurrency: 'Валюта документа',
      bookingRate: 'Курс на {{date}}',
      bookingNote:
        'Курс фиксируется при создании счёта и потом не пересчитывается. Нужна другая валюта — это новый счёт.',

      prints: 'Что напечатается',

      next: 'Что дальше',
      nextOne: 'Черновик правится сколько угодно.',
      nextTwo:
        'PDF собирается и ложится в архив работы. Любая правка черновика отменяет его.',
      nextThree:
        'Выпуск присваивает номер, закрывает транш и создаёт дебиторку. Отменить нельзя.',

      faceValue: 'Сумма счёта',
      received: 'Поступило',
      outstanding: 'Остаток долга',
    },

    /* Счета клиенту по работе (история 5.4, FR-61).
     *
     * «Счёт» здесь — бумага, которая уходит клиенту, а не архивная запись под
     * ней: это две разные записи, и словарь тоже должен оставаться разным. */
    invoices: {
      title: 'Счета клиенту',
      new: 'Новый счёт',
      newTitle: 'Новый счёт по {{number}}',
      raise: 'Создать черновик',

      caseLabel: 'Работа',
      casePlaceholder: 'Выберите работу',
      /* Названо правилом, а не серой кнопкой: счёт выставляется по работе, и
         здесь сказано, какой шаг идёт первым. */
      chooseCaseFirst: 'Сначала выберите работу — счёт всегда выставляется по ней.',
      noCaseTitle: 'Выберите работу',
      noCaseDescription:
        'Счёт выставляется по работе: вместе с ней приходят клиент, котировка и согласованная ставка. Выберите работу выше.',
      casesUnavailable: 'Список работ не загрузился — выбирать пока не из чего.',
      emptyTitle: 'По этой работе счетов нет',
      emptyDescription:
        'Создайте счёт, чтобы выставить его клиенту. По одной работе их может быть несколько — по счёту на каждый транш оплаты.',
      /* У черновика номера нет. Его присваивает история 5.5 при выставлении, а
         всё, что напечатано здесь до этого, не принадлежит ни одной серии
         (AD-12). */
      unnumbered: 'Номера пока нет',
      open: 'Открыть счёт {{invoice}} от {{date}}',

      invoiceDate: 'Дата счёта',
      economicDate: 'Экономическая дата',
      economicDateHint: 'Дата, на которую берётся курс.',
      /* Сказано, а не спрятано: проводка — это пять фактов, зафиксированных
         вместе (AD-9), и поле, тихо исчезнувшее после сохранения, выглядело бы
         ошибкой, а не правилом. */
      economicDateFrozen: 'Зафиксирована вместе с курсом. Чтобы изменить — создайте новый счёт.',
      customerReference: 'Ваш реф.',
      customerReferenceHint: 'Собственный номер клиента — так он и печатается в счёте.',

      currency: 'Валюта документа',
      currencyHint:
        'Выбирается один раз. Вместе с ней фиксируются курс и эквивалент в USD, и больше они не меняются.',

      vat: 'НДС',
      vatApplies: 'НДС применяется',
      /* FR-10: блок либо печатается, либо опускается, но никогда не печатается
         нулём — нулевая строка НДС говорит о необлагаемой поставке. */
      vatHint: 'Налоговый блок печатается только при включённой галочке.',
      vatRate: 'Ставка НДС, %',
      vatRateHint: 'В процентах: 20 означает 20%.',
      vatAmount: 'Налог по этому счёту: {{amount}}',

      paymentTerms: 'Условия оплаты',
      bankDetails: 'Банковские реквизиты',
      /* FR-64/AC-5: на счёте их не набирают. Указание источника — то, что
         останавливает поиск поля для правки. */
      termsNote:
        'Условия оплаты и банковские реквизиты подставляются из юрлица и здесь не редактируются. Измените их в настройках — и они изменятся во всех счетах.',
      saveHeader: 'Сохранить шапку',

      lines: {
        label: 'Позиции',
        empty:
          'Позиций пока нет. Работа из котировки приходит с позициями, которые принял клиент; работа, заведённая напрямую, начинается с пустого списка.',
        description: 'За что начислено',
        quantity: 'Кол-во',
        unitPrice: 'Цена',
        amount: 'Сумма',
        amountHint: 'Сумма к выставлению, в {{currency}}.',
        amountPositive: 'Введите сумму больше нуля',
        /* Видно, только когда сумма НЕ равна произведению колонок рядом —
           значит её вписали руками поверх расчёта. Это законно (лот, круглая
           цифра, договорённость по телефону), но сказать об этом стоит: так же
           выглядит и наполовину доведённая правка. В обычном случае произведение
           уже стоит в самом поле, и подсказка, повторяющая своё же поле, учит
           не читать подсказки. */
        amountPreview: 'Не совпадает с расчётом: кол-во × цена = {{amount}}',
        add: 'Добавить позицию',
        saveCharge: 'Сохранить позицию',
        editN: 'Изменить позицию {{n}}',
        remove: 'Удалить позицию {{n}}',
        /* AD-10: отказ приходит в момент ДОБАВЛЕНИЯ позиции, а не при
           выставлении. Цифры собираются здесь, из счёта, который уже на
           экране, — сервер намеренно не присылает их внутри ошибки. */
        exceedsApprovedRate:
          'С этой позицией выставленная сумма превысит согласованную ставку {{approved}} на {{excess}}. По этой работе уже выставлено {{billed}}. Сначала нужно согласовать изменение ставки.',
        exceedsShort: 'Выше согласованной ставки',
        exceedsUnknownRate:
          'Эта позиция вывела бы сумму к выставлению за согласованную ставку по этой работе. Сначала нужно согласовать изменение ставки.',
      },
    },
  },

  clients: {
    title: 'Клиенты',
    subtitle: 'Кто сколько должен и какие перевозки стоят за цифрой.',
    new: 'Новый клиент',
    year: { label: 'Год выписки', all: 'Все годы' },
    tiles: {
      billed: 'Начислено',
      received: 'Получено',
      balance: 'Остаток',
      balanceToday: 'Остаток на сегодня',
      opening: 'Остаток прошлого периода',
      cost: 'Себестоимость',
    },
    filters: { withBalance: 'Только с остатком' },
    columns: {
      client: 'Клиент',
      managers: 'Менеджер',
      cases: 'Работ',
      weight: 'Вес, кг',
      billed: 'Начислено',
      received: 'Получено',
      balance: 'Остаток',
      cost: 'Себестоимость',
      status: 'Статус',
    },
    table: { label: 'Клиенты', emptyTitle: 'Клиентов пока нет', emptyDescription: 'Добавьте первого клиента — с него начинается каждая котировка.' },
    actions: { edit: 'Изменить', merge: 'Слить', open: 'Открыть клиента' },
    moneyUnavailable: {
      title: 'Цифры временно недоступны',
      body: 'Справочник полный, а деньги сейчас прочитать не удалось. Попробуйте через минуту.',
    },
    statement: {
      title: 'Выписка',
      eyebrow: 'Клиент',
      tableLabel: 'Выписка по клиенту {{name}}',
      openingRow: 'Остаток прошлого периода',
      totalRow: 'Итого',
      emptyTitle: 'Перевозок за этот год нет',
      emptyDescription: 'Выберите другой год или все годы.',
      details: 'Реквизиты и контакты',
      columns: {
        no: '№',
        openedOn: 'Дата приёма',
        number: 'Работа',
        route: 'Маршрут',
        mode: 'Транспорт',
        cargo: 'Груз',
        weight: 'Вес, кг',
        volume: 'Объём, м³',
        billed: 'Начислено',
        received: 'Получено',
        running: 'Остаток',
        paymentMethod: 'Оплата',
        lastReceivedOn: 'Дата оплаты',
        ata: 'Прибытие',
        transitDays: 'Дней',
        owner: 'Менеджер',
        cost: 'Себестоимость',
        status: 'Статус',
      },
    },
  },

  directories: {
    title: 'Справочники',
    subtitle: 'Записи, к которым привязана каждая сделка, в одном месте.',
    sectionsLabel: 'Разделы справочников',

    tabs: {
      clients: 'Клиенты',
      participants: 'Участники',
      users: 'Пользователи',
      transport: 'Транспорт',
    },

    /* Отказ в удалении (FR-39, история 7.1 AC 4, история 7.4 AC 5).
     *
     * Номера подставляются, а не склеиваются на месте вызова: в русской фразе
     * список стоит в другом месте, и предложение, собранное из обрывков, его
     * туда не перенесёт (UX-DR-30). */
    referenced: {
      // `more`, а не `count`: i18next считает `count` признаком множественного
      // числа и стал бы искать здесь andMore_one/andMore_other, а рядом — четыре
      // русские формы, которые этой фразе не нужны.
      andMore: '{{named}} и ещё {{more}}',
      mergeInstead:
        'Запись используется в {{list}}, поэтому удаление оставит их без ссылки. Объедините её с другой записью.',
      deactivateInstead:
        'Запись используется в {{list}}, поэтому удалить её нельзя. Деактивируйте её — она перестанет предлагаться в новых записях и останется в тех, где уже указана.',
      // Объединение, которое не может выполниться: в этой установке нечем
      // перенести ссылки. Формулировка отличается намеренно — она не должна
      // отправлять читателя к действию, которое только что отказало.
      mergeUnavailable:
        'Запись используется в {{list}}, и эта установка пока не умеет переносить такие ссылки — объединение оставит их без ссылки. Деактивируйте её.',
    },

    /* Объединение одной записи с другой — действие, которое FR-39 предлагает
     * вместо отклонённого удаления. */
    merge: {
      action: 'Объединить',
      title: 'Объединить с другой записью',
      description:
        'Всё, что ссылается на эту запись, будет ссылаться на выбранную, а эта запись будет удалена. Отменить нельзя.',
      target: 'Объединить с',
      targetPlaceholder: 'Выберите запись, которая останется',
      confirm: 'Объединить и удалить',
      sameRecord: 'Выберите другую запись для объединения.',
    },

    activate: 'Активировать',
    deactivate: 'Деактивировать',
    // Причина отказа — во всплывающей подсказке, а не через disabled: у
    // отключённой кнопки подсказка недоступна при наведении (UX-DR-22).
    needsGrant: 'Для этого списка нужен грант на справочник.',

    /* Блок контактов, общий для клиентов и участников (FR-39, FR-41). */
    contacts: {
      legend: 'Контакты',
      label: 'Кто это',
      labelPlaceholder: 'например, отдел логистики',
      add: 'Добавить контакт',
      remove: 'Удалить контакт',
      primary: 'Основной контакт',
      handlePlaceholder: 'ник или ссылка на профиль',
      /* Сказано прямо: поле, похожее на канал доставки, будут использовать
         как канал доставки. */
      messengerNote:
        'Только контактные данные. Платформа не отправляет сюда сообщения — переписка ведётся вне неё.',
      restricted: 'Контактные данные недоступны для вашей роли.',
    },

    clients: {
      title: 'Клиенты',
      new: 'Новый клиент',
      delete: 'Удалить',
      edit: 'Изменить клиента',
      name: 'Название',
      country: 'Страна',
      taxId: 'ИНН',
      registrationId: 'ОГРН / рег. номер',
      address: 'Адрес',
      code: 'Код клиента',
      /* Подставляется в номера документов (FR-39, FR-68), поэтому стоит
         знать, где он появится. */
      codeHint: 'Необязательно. Может подставляться в номера документов — держите его коротким.',
      codeInvalid: 'Только буквы, цифры и дефис',
      contact: 'Контакт',
      owner: 'Ответственный',
      open: 'В работе',
      receivable: 'Дебиторка',
    },

    participants: {
      title: 'Участники',
      new: 'Новый участник',
      edit: 'Изменить участника',
      type: 'Тип',
      wechat: 'WeChat',
      name: 'Название',
      country: 'Страна',
      owner: 'Ответственный',
      contact: 'Контакт',
    },

    users: {
      title: 'Пользователи',
      new: 'Новый пользователь',
      edit: 'Изменить пользователя',
      email: 'Email',
      invalidEmail: 'Введите корректный email',
      role: 'Роль',
      office: 'Офис',
      grants: 'Права',
      grantLabels: {
        manageClients: 'Ведение клиентов',
        manageParticipants: 'Ведение участников',
        manageTransport: 'Ведение справочника транспорта',
      },
      status: 'Статус',
      inactive: 'Неактивен',
      language: 'Язык интерфейса',
      /* Адрес — идентификатор учётной записи; его смена переписала бы
         авторство всех прошлых записей (история 1.3). */
      emailFixed: 'Email нельзя изменить после создания учётной записи.',
      awaitingPasswordChange: 'Пароль ещё не сменён',
      resetPassword: 'Сбросить пароль',
      deactivate: 'Деактивировать',
      activate: 'Активировать',
      /* Передача пароля. Пароль уходит владельцу письмом и, кроме того,
         показывается здесь ровно один раз — на сервере хранится только его
         хэш. В тексте есть оба факта: экран остаётся запасным вариантом,
         когда адрес указан неверно или почта не работает. */
      credentialTitle: 'Пароль для {{name}}',
      credentialIntro:
        'Учётная запись готова к работе. Пароль отправлен письмом на {{email}} вместе со ссылкой для входа и показан здесь на случай, если передать его нужно лично.',
      credentialCopy: 'Копировать',
      credentialCopied: 'Скопировано',
      credentialWarning:
        'Здесь пароль показывается один раз и не восстанавливается. При первом входе система попросит владельца сменить его. Если письмо не пришло, сбросьте пароль — новый уйдёт письмом.',
      credentialDone: 'Я сохранил пароль',
    },

    transport: {
      title: 'Транспорт',
      modes: 'Виды перевозки',
      equipmentTypes: 'Типы оборудования',
      add: 'Добавить',
      mode: 'вид перевозки',
      equipment: 'тип оборудования',
      addItem: 'Новый — {{item}}',
      editItem: 'Изменить — {{item}}',
      code: 'Код',
      codeHint: 'Короткий машинный код, например FCL или 40HC.',
      delete: 'Удалить',
    },

    editRecord: 'Изменить — {{name}}',
  },

  settings: {
    title: 'Настройки',
    subtitle: 'Как документы нумеруются, брендируются и формулируются.',
    sectionsLabel: 'Разделы настроек',

    tabs: {
      entities: 'Юрлица',
      numbering: 'Нумерация',
      templates: 'Шаблоны',
      directions: 'Направления',
      access: 'Роли и доступ',
    },

    /* Роли и права — редактируемая модель миграции 0021. */
    roles: {
      title: 'Роли',
      intro:
        'Роль — это набор прав, который вы называете сами. У сотрудника одна роль плюс права, выданные ему лично.',
      create: 'Новая роль',
      loading: 'Загрузка ролей',
      edit: 'Изменить «{{name}}»',
      editAction: 'Изменить «{{name}}»',
      deleteAction: 'Удалить «{{name}}»',
      loadFailed: 'Не удалось загрузить роли. Попробуйте ещё раз.',
      deleteFailed:
        'Роль не удалена. Сначала переведите сотрудников, которые её занимают, на другую роль.',
      superuserLocked:
        'У этой роли всегда есть все права, отмечать нечего. Именно поэтому она может восстановить остальные.',
      badges: {
        superuser: 'Полный доступ',
        system: 'Встроенная',
      },
      summary: {
        allPermissions: 'Все права',
        counts: 'Прав: {{capabilities}} · Разделов: {{surfaces}}',
        users_one: '{{count}} сотрудник',
        users_few: '{{count}} сотрудника',
        users_many: '{{count}} сотрудников',
      },
      fields: {
        key: 'Идентификатор',
        keyHint: 'Строчные латинские буквы, цифры и дефисы. Изменить позже нельзя.',
        name: 'Название',
        description: 'Описание',
        descriptionHint: 'Для чего нужна эта роль — своими словами.',
      },
      scopes: {
        title: 'Что видно',
        hint: 'Чьи записи доступны этой роли. Если поставить «Нет доступа», раздел полностью исчезнет из навигации.',
      },
      capabilities: {
        title: 'Что можно делать',
        hint: 'Каждое право выключено, пока вы его не включите.',
      },
      levels: {
        none: 'Нет доступа',
        own: 'Только свои',
        assigned: 'Свои и те, где участвует',
        all: 'Все',
      },
      surfaces: {
        quotes: 'Котировки',
        cases: 'Работы',
        'case-money': 'Затраты и прибыль по работе',
        'cross-case-finance': 'Финансы компании',
        clients: 'Клиенты',
        participants: 'Участники',
      },
      groups: {
        quote: 'Котировки',
        case: 'Работы',
        directory: 'Справочники',
        finance: 'Финансы',
        document: 'Документы',
        collab: 'Файлы и чат',
        admin: 'Администрирование',
      },
    },
    access: {
      title: 'Матрица доступа',
      subtitle:
        'Что каждая роль видит и может делать. Роли фиксированы; человеку роль назначается в его карточке пользователя.',
      tableCaption: 'Доступ по группам данных и ролям',
      dataGroup: 'Группа данных',

      levels: {
        full: 'Все',
        scoped: 'Свои',
        read: 'Чтение',
        hidden: 'Скрыто',
        none: 'Нет доступа',
      },

      detail: {
        ownOnly: 'Только свои',
        ownDeals: 'Свои сделки',
        ownQuotes: 'Свои котировки',
        assigned: 'Где назначен',
        readCreateOwn: 'Чтение и создание своих',
        salesDirectorGrant: 'Директор по продажам — по праву',
        pricingDirectorGrant: 'Директор по расчётам — по праву',
        maintainAgents: 'Ведёт агентов',
        participantsSales:
          'Агенты скрыты; отправитель, получатель и водитель видны на своих работах',
      },

      groups: {
        operational: 'Котировки и работы — операционные данные',
        commercial: 'Коммерция по сделке — затраты, наценка, ставка, расходы',
        agentIdentity: 'Личность агента в полях',
        crossCaseFinance: 'Финансы по всем работам — сроки, балансы, общая прибыль',
        pipeline: 'Воронка котировок и статистика побед',
        clients: 'Справочник клиентов',
        participants: 'Справочник агентов и участников',
      },

      enforcementNote:
        'Проверяется на сервере при каждом запросе. То, что скрывает интерфейс, — это удобство, а не сама граница.',

      grantsTitle: 'Права ведения',
      grantsSubtitle:
        'Дополнение к роли, а не отдельные роли. Право расширяет справочник; доступ к работам и деньгам оно не расширяет никогда.',
      grants: {
        manageClients:
          'Ведение всего списка клиентов, включая чужие записи, — со слиянием и удалением.',
        manageParticipants:
          'Ведение всего списка участников, включая агентов, — со слиянием и удалением.',
        manageTransport: 'Ведение видов транспорта и типов оборудования.',
      },
      grantsNote:
        'Выдаётся каждому отдельно в разделе «Справочники → Пользователи». У основателей есть все три.',

      boundariesTitle: 'Границы, которые стоит проговорить',
      boundaries: {
        ownDealMoney: {
          title: 'Команда сделки видит деньги своей сделки',
          body:
            'Продажи сами вносят ставку и ожидаемые затраты — значит, и видят их. Ограничение «только основатели» касается финансов по всем работам: дашборда, сроков и балансов контрагентов, а не собственной сделки.',
        },
        agentIdentityScope: {
          title: 'Скрытие агента касается только полей',
          body:
            'Записи справочника, слоты участников и метки поставщиков для продаж скрыты и показывают «Агент скрыт», а не пустоту. Загруженные файлы и чат по работе не редактируются.',
        },
      },
      externalTag: 'Не пользователи',
      externalNote:
        'Клиенты, отправители, получатели, агенты, владельцы транспорта, диспетчеры и водители не входят в систему. Они получают только письма.',
    },

    entities: {
      title: 'Юрлица',
      new: 'Новое юрлицо',
      edit: 'Изменить юрлицо',
      name: 'Название юрлица',
      legalFooter: 'Юридический футер',
      /* Два коротких кода, которые подставляются в шаблон номера (FR-68). */
      deactivate: 'Деактивировать',
      activate: 'Активировать',
      inactive: 'Деактивировано',
      /* Незаполненный футер или реквизиты уходят клиенту пустой строкой. */
      notSet: 'Не задано',
      initials: 'Инициалы',
      officeCode: 'Код офиса',
      defaultOffice: 'По умолчанию для офиса',
      defaultOfficeHint:
        'Записи, созданные пользователями этого офиса, выпускаются от этого юрлица. Не более одного юрлица на офис.',
      noDefaultOffice: 'Ни один офис не использует это юрлицо',
      legalFooterHint: 'Печатается внизу каждого документа этого юрлица.',
      bankDetails: 'Банковские реквизиты',
      emailSignature: 'Подпись в письмах',

      /* Загруженный логотип (FR-64, миграция 0041). До него каждый документ
         печатал НАЗВАНИЕ юрлица там, где должен быть логотип. */
      logo: {
        upload: 'Загрузить логотип',
        replace: 'Заменить логотип',
        remove: 'Удалить',
        sending: 'Логотип отправляется…',
        /* Говорит о том, что сделает документ, а не о том, что принимает форма:
           читателя интересует, что получит клиент. */
        hint: 'PNG или JPEG, до 2 МБ. Печатается на каждом предложении, счёте и дебет-ноте этого юрлица. Без него документы несут название юрлица.',
        errorType: 'Файл такого типа нельзя напечатать на документе. Используйте PNG или JPEG.',
        errorSize: 'Логотип больше 2 МБ. Экспортируйте его меньшего размера и попробуйте снова.',
        errorUpload: 'Не удалось отправить логотип в хранилище. Проверьте соединение и попробуйте снова.',
        errorGeneric: 'Не удалось сохранить логотип.',
      },
    },

    numbering: {
      title: 'Схемы нумерации',
      new: 'Новая схема',
      edit: 'Изменить схему',
      entity: 'Юрлицо',
      document: 'Документ',
      pattern: 'Шаблон',
      kinds: {
        quote: 'Котировка',
        case: 'Работа',
        invoice: 'Инвойс',
        'debit-note': 'Дебет-нота',
      },
      /* Показывается, когда шаблон не задан и действует значение по умолчанию. */
      usingDefault: 'По умолчанию',
      /* Сервер отклоняет шаблон стабильным кодом; здесь его переводы (AD-27). */
      errors: {
        missingSeq: 'В шаблоне нужен {seq}, иначе все записи получат один номер.',
        unknownToken:
          'Неизвестный токен. Используйте {INITIALS}, {OFFICE}, {YY}, {YYYY}, {MM} или {seq}.',
        seqTooWide: 'Слишком широкое дополнение нулями.',
        invalid: 'Такой шаблон использовать нельзя.',
      },
      liveExample: 'Живой пример',
      next: 'Следующий',
      changeNote:
        'Изменение шаблона действует на документы, выпущенные с этого момента. Уже выпущенные номера сохраняют свою форму.',
    },

    templates: {
      /* Четыре группы, и печатаются только первые три. Формулировки эту разницу
         сохраняют: три печатных — текст в документе, заготовки — список, с
         которого начинается расчёт себестоимости. */
      included: 'Включено',
      notIncluded: 'Не включено',
      disclaimers: 'Оговорки',
      costItems: 'Заготовки статей затрат',
      costItemsHint:
        'Статьи затрат, с которых начинается расчёт. В КП не печатаются и сумм не содержат — деньги вносятся в каждой котировке отдельно.',
      new: 'Новый шаблон',
      edit: 'Изменить шаблон',
      label: 'Название',
      body: 'Текст',
      bodyHint: 'Попадает в КП дословно. Пишите так, как это прочитает клиент.',
      bodyHintCostItem:
        'Необязательно. Пометка для того, кто считает цену, — за пределы компании не выходит.',
      /* Почему шаблон удаляется полностью, тогда как записи справочников только
         деактивируются: миграция 0033 копирует текст в саму котировку, поэтому
         удаление не меняет ни один уже отправленный документ. */
      removeHint: 'Удаление шаблона не меняет уже отправленные предложения.',
    },

    /* История 2.9, критерий 4. Каждая подсказка называет того, кто читает
       значение, включая два, которые пока не читает никто, — настройка, молча
       ни на что не влияющая, учит основателя, что элементы управления
       декоративны. */
    thresholds: {
      title: 'Пороги напоминаний и внимания',
      configured: 'Значения заданы для этой компании.',
      usingDefaults: 'Значения ещё не сохранены, поэтому действуют значения по умолчанию.',
      followUp: 'Напоминание о повторном обращении',
      followUpHint: 'Дней после отправки предложения без ответа',
      validityWarning: 'Предупреждение об истечении срока',
      validityWarningHint: 'Дней до даты окончания действия котировки',
      stuckShipment: 'Задержанная перевозка',
      stuckShipmentHint: 'Дней, которые работа может простоять без движения',
      defaultIs: 'по умолчанию {{days}}',
      range: 'От {{min}} до {{max}} дней',
      wholeDays: 'Введите целое число дней.',
      outOfRange: 'Вне диапазона, который принимает эта настройка.',
      awaitingReminderJob: 'пока не читается ни одной задачей напоминаний (история 2.8)',
      readByDashboardEnv:
        'панель пока читает ASA_DASHBOARD_STUCK_DAYS вместо этого',
    },

    /* Каталог направлений (issue #35, история 1). Именованный маршрут — это
       упорядоченный список точек, у каждой страна и норматив перехода в днях.
       Слово «шаблон» на этом экране не используется намеренно: в работе это
       просто маршруты на выбор, а единственное, что Супер-админ обязан
       понимать до правки, — применение КОПИРУЕТ маршрут. Об этом прямо сказано
       в подзаголовке, а не подразумевается словом. */
    directions: {
      title: 'Направления',
      subtitle:
        'Именованные маршруты, из которых заполняется направление работы. При применении точки копируются в направление, поэтому правка справочника не сдвигает уже идущую перевозку.',
      empty: 'Направлений пока нет.',
      new: 'Новое направление',
      edit: 'Изменить направление',
      inactive: 'выключено',
      activate: 'Включить',
      deactivate: 'Выключить',
      pointCount_one: '{{count}} точка',
      pointCount_few: '{{count}} точки',
      pointCount_many: '{{count}} точек',
      normDays_one: 'норматив {{count}} день',
      normDays_few: 'норматив {{count}} дня',
      normDays_many: 'норматив {{count}} дней',
      name: 'Название',
      nameHint: 'Как его называют на месте — «Хоргос → Ашхабад (через Сарахс)».',
      points: 'Точки маршрута',
      pointsHint: 'В порядке следования. Первая точка — там, где груз начинает путь.',
      pointPlaceholder: 'Хоргос',
      pointName: 'Точка {{n}}',
      pointCountry: 'Страна точки {{n}}',
      pointDays: 'Срок до точки {{n}}, дней',
      daysPlaceholder: 'дней',
      firstPointStart: 'старт',
      addPoint: 'Добавить точку',
      removePoint: 'Удалить точку {{n}}',
      maxPoints: 'В маршруте не больше {{max}} точек.',
      daysNote:
        'Срок в днях задаёт переход от предыдущей точки. По его истечении позиция груза в кейсе передвигается автоматически.',
      delete: 'Удалить направление',
      deleteConfirm:
        'Удалить? Кейсы, заполненные из него, хранят свою копию и не изменятся.',
      deleteConfirmAction: 'Удалить',
      errors: {
        minPoints: 'В направлении должно быть не меньше двух точек.',
        wholeDays: 'Введите целое число дней.',
      },
    },
  },

  auth: {
    /* Вход в систему (FR-57, Story 1.2). Почта и пароль, без 2FA и SSO в v1.
     *
     * Текст ошибки намеренно ОБЩИЙ: сервер отвечает одинаково независимо от
     * того, существует ли такая почта, чтобы по форме входа нельзя было
     * узнать, у кого есть учётная запись. Отдельной строки «такой почты нет»
     * здесь нет и быть не должно — она вернула бы ровно то, что сервер скрыл. */
    signIn: {
      title: 'Вход',
      subtitle: 'Расчёты, работы и финансы команды ASA.',
      email: 'Почта',
      emailPlaceholder: 'you@asagateway.com',
      password: 'Пароль',
      submit: 'Войти',
      pending: 'Выполняется вход',
      showPassword: 'Показать пароль',
      hidePassword: 'Скрыть пароль',
      failed: 'Почта и пароль не совпадают',
      failedDetail: 'Проверьте оба поля и повторите.',
      throttled: 'Слишком много попыток',
      throttledDetail: 'Вход для этой почты приостановлен на 15 минут.',
      expired: 'Сеанс завершён',
      expiredDetail: 'Войдите снова, чтобы продолжить работу.',
      /* Самостоятельного сброса пароля в v1 нет — его делает Супер-админ. */
      forgotten: 'Забыли пароль? Супер-админ сбросит его для вас.',
    },
    validation: {
      emailRequired: 'Введите почту',
      emailInvalid: 'Введите корректный адрес почты',
      passwordRequired: 'Введите пароль',
    },
    signOut: 'Выйти',
  },

  profile: {
    title: 'Профиль',
    subtitle: 'Ваша учётная запись, роль и язык, на котором с вами говорит интерфейс.',
    loading: 'Загрузка профиля',

    photo: {
      change: 'Изменить фото',
      remove: 'Убрать',
      hint: 'JPEG, PNG, WebP или GIF, до 5 МБ.',
      uploading: 'Отправляем фото…',
      failed: 'Фото не сохранено. Повторите.',
      removeFailed: 'Фото не убрано. Повторите.',
      rejected: {
        type: 'Такой файл нельзя использовать как фото. Выберите JPEG, PNG, WebP или GIF.',
        size: 'Фото больше 5 МБ. Выберите файл поменьше.',
      },
    },

    language: {
      title: 'Язык',
      description:
        'Язык интерфейса для вашей учётной записи. Документы клиентам всегда на английском; тексты писем можно писать на любом языке.',
      error: 'Язык не изменён. Повторите.',
    },

    access: {
      title: 'Доступ',
      description:
        'Что доступно вашей учётной записи. Роли и права на справочники назначает суперадмин в вашей карточке пользователя в разделе <0>Справочники</0>.',
      noGrants:
        'У вас нет прав на ведение справочников. На доступ вашей роли это не влияет.',
      grants: {
        manageClients: 'Ведёт справочник клиентов',
        manageParticipants: 'Ведёт участников и агентов',
        manageTransport: 'Ведёт справочник транспорта',
      },
    },
  },

  notFound: {
    title: 'Не найдено',
    heading: 'Страница недоступна',
    description:
      'Ссылка могла устареть, либо запись принадлежит другой команде.',
  },

  errors: {
    forbidden: {
      title: 'Раздел недоступен для вашей роли',
      detail: 'Обратитесь к суперадмину, если нужен доступ.',
    },
    notFound: {
      title: 'Запись недоступна',
      detail: 'Она могла быть удалена либо принадлежит другой команде.',
    },
    server: {
      title: 'Сервер не ответил',
      detail: 'Данные не изменены. Повторите через минуту.',
    },
    network: {
      title: 'Не удалось загрузить данные',
      detail: 'Проверьте соединение и повторите.',
    },
    /* Сбой самого интерфейса, а не запроса. В отличие от `server` выше, здесь
       НЕЛЬЗЯ писать «данные не изменены»: упавший рендер ничего не знает о
       том, что дошло до сервера, а обещание, которое мы не можем подтвердить,
       хуже молчания. Говорим, что делать. */
    crash: {
      title: 'Что-то пошло не так',
      heading: 'Не удалось показать этот экран',
      detail:
        'Повторите попытку или обновите страницу. Если повторяется — передайте это администратору и опишите, что вы делали.',
      reload: 'Обновить страницу',
      details: 'Технические детали',
    },
    validation: {
      detail: 'Проверьте значения и повторите.',
    },
    /* Отказ по FR-64, в двух видах, для трёх листов, которым он может прийти:
       КП, счёт или дебет-нота по делу и счёт клиенту. Одна формулировка, а не
       три: подлежащее фразы — ЮРЛИЦО и экран, где это чинится, а не бумага, в
       которой отказали. Назвать документ — значит написать три фразы ради
       одного действия, и читатель и так смотрит на тот лист, который не прошёл.
       Сервер называет поля, которые у юрлица не заполнены, поэтому обычная
       фраза — та, что их перечисляет: «банковские реквизиты» и «юридический
       футер» — это два разных вечера в настройках, а одна фраза на оба
       случая называла футер там, где с футером всё было в порядке.
       Фраза без перечисления остаётся на случай, когда полей в отказе не
       было: старый сервер или ключ поля, слов для которого в этом каталоге
       нет. Она всё равно ведёт в настройки — чинить всё равно там, — и
       намеренно ничего не называет. */
    branding: {
      incomplete: 'У юрлица не заполнены реквизиты документов — без них PDF не выпускается. Супер-админ может заполнить их в Настройках → Юрлица.',
      incompleteFields: 'У юрлица не заполнено: {{fields}}. Без этого PDF не выпускается — заполнить может супер-админ в Настройках → Юрлица.',
      /* Имена полей приходят машинными ключами (`legal-footer`), слова — наши
         (AD-27). Именительный падеж: из них собирается перечисление после
         двоеточия, и в любом другом падеже список пришлось бы согласовывать с
         фразой, которая его держит. */
      field: {
        name: 'название',
        logo: 'логотип',
        legalFooter: 'юридический футер',
        bankDetails: 'банковские реквизиты',
      },
    },
    /* По одной записи на каждый суффикс из реестра сервера
       (platform/httpx/problem.go). Суффикс — это контракт; формулировки наши,
       потому что title и detail в problem-документе написаны для разработчика,
       читающего лог (AD-22, AD-27). */
    problems: {
      validationFailed: {
        title: 'Некоторые значения требуют внимания',
        detail: 'Выделенные поля не приняты.',
      },
      authRequired: {
        title: 'Сессия завершена',
        detail: 'Войдите снова, чтобы продолжить. Введённое не отправлено.',
      },
      forbidden: {
        title: 'Недоступно для вашей роли',
        detail: 'Обратитесь к супер-администратору, если нужен доступ.',
      },
      notFound: {
        title: 'Запись недоступна',
        detail: 'Возможно, она удалена или принадлежит другой команде.',
      },
      conflict: {
        title: 'Изменение конфликтует с записью',
        detail: 'Обновите страницу, чтобы увидеть текущее состояние, и повторите.',
      },
      idempotencyConflict: {
        title: 'Это уже было отправлено',
        detail: 'Действует предыдущая отправка. Обновите страницу.',
      },
      rateLimited: {
        title: 'Слишком много попыток',
        detail: 'Подождите немного и повторите.',
      },
      payloadTooLarge: {
        title: 'Слишком большой объём',
        detail: 'Уменьшите размер и повторите.',
      },
      unsupportedMediaType: {
        title: 'Такой тип файла не принимается',
        detail: 'Используйте один из поддерживаемых форматов.',
      },
      unavailable: {
        title: 'Здесь это недоступно',
        detail: 'Функции нужен сервис, который не настроен. Обратитесь к администратору.',
      },
      internal: {
        title: 'Сервер не смог ответить',
        detail: 'Ничего не изменено. Повторите через минуту.',
      },
      unknown: {
        title: 'Что-то пошло не так',
        detail: 'Повторите попытку. Если повторяется — сообщите супер-администратору.',
      },
    },

    /* Строка состояния соединения. Постоянная, не всплывающая: уведомление,
       которое исчезает при потере связи, хуже, чем ничего (UX-DR-18). */
    connection: {
      offline: 'Нет соединения. Введённое сохранено на экране и не отправлено.',
      restored: 'Соединение восстановлено.',
      retry: 'Повторить',
    },
    requestFailed: 'Запрос не выполнен',
  },
} as const
